import React, { forwardRef, RefObject, useMemo } from 'react';
import classnames from 'classnames';
import SVG, { SVGProps } from './SVGWrapper';
import { Chevron, IconCircle, PartialCircle, Thumb } from './GraphicalElements';
import styles from './Icons.module.scss';
import Contact from '@models/Contact';
import { useSelector } from 'react-redux';
import { isStarredSelector } from '@actions/starred';
import { useTheme } from '@hooks/use-theme';

export const ActionCopyIcon = ({
  width = 14,
  height = 14,
  color = 'primary',
  hoverOpacity = true,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color, hoverOpacity }}
    className={classnames('icon action-copy-icon', props.className)}
    viewBox="0 0 16 16"
  >
    <g strokeWidth="1.33" fill="none">
      <rect x="5.35" y="2" width="8.64" height="8.64" rx="2" />
      <path
        strokeLinecap="round"
        d="M10.4 12.98c-.17.32-.43.58-.76.75l-.12.06c-.3.14-.64.21-1.42.21H4.56c-.89 0-1.21-.1-1.54-.27a1.82 1.82 0 0 1-.75-.75l-.06-.13c-.13-.27-.2-.58-.2-1.25L2 7.9c0-.9.1-1.22.27-1.54.17-.33.43-.59.75-.76l.13-.06"
      />
    </g>
  </SVG>
);

export const PenIcon = ({
  width = 24,
  height = 24,
  color = 'primary',
  hoverOpacity = true,
  strokeWidth = 2,
  ...props
}: SVGProps) => (
  <SVG
    viewBox="0 0 24 24"
    {...{ ...props, width, height, color, hoverOpacity }}
    className={classnames('icon pen-icon', props.className)}
  >
    <path
      strokeWidth={strokeWidth}
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M16.92 4.1a3 3 0 0 1 2.9 5.03l-9.97 9.98L4 20.71l1.6-5.85 9.97-9.98a3 3 0 0 1 1.35-.78Z"
    />
  </SVG>
);

export const ActionGroupsIcon = ({
  width = 24,
  height = 24,
  color = 'primary',
  hoverOpacity = true,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color, hoverOpacity }}
    className={classnames('icon action-groups-icon', props.className)}
    viewBox="0 0 24 24"
  >
    <g strokeLinecap="round">
      <circle strokeWidth="1.7" cx="8.6" cy="14" r="7" fill="none" />
      <path
        strokeWidth="1.7"
        d="M10.1 7.1V4.9a2.4 2.4 0 012.4-2.4h7.4a2.4 2.4 0 012.4 2.4V12.3a2.4 2.4 0 01-2.4 2.4H15.6"
        fill="none"
      />
      <path
        strokeWidth="1.5"
        d="M14.7 10.7a2.3 2.3 0 003.4.0l.3-.3h0M14.2 5.5v1.1m4.2-1.1v1.1M6.4 15.9l.128.1a2.9 2.9 0 004.1-.1h0M6.1 11.9v1.1m4.9-1.1v1.1"
        fill="none"
      />
    </g>
  </SVG>
);

export const ActionMergeIcon = ({
  width = 24,
  height = 24,
  color = 'primary',
  hoverOpacity = true,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color, hoverOpacity }}
    className={classnames('icon action-merge-icon', props.className)}
    viewBox="0 0 24 24"
  >
    <path
      strokeWidth="2"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M6.9 17.6L18.7 5.8M10.1 9.2H5h5.1V4.2zm5 5.2v5.1-5.1h5.1z"
    />
  </SVG>
);

export const ActionStarredIcon = ({
  width = 24,
  height = 24,
  color = 'primary',
  hoverOpacity = true,
  contact = null,
  strokeWidth = 1.6,
  ...props
}: SVGProps & { contact?: Contact }) => {
  const isStarred = useSelector(isStarredSelector(contact));
  const colorOverride = isStarred ? 'primary' : color;

  return (
    <SVG
      {...{ ...props, width, height, color: colorOverride, hoverOpacity }}
      className={classnames(
        'icon action-starred-icon',
        props.className,
        styles.actionStarredIcon,
        isStarred && styles.actionStarredIconActive,
      )}
      viewBox="0 0 24 24"
    >
      <defs>
        <path
          id="star"
          strokeWidth={strokeWidth}
          strokeLinejoin="round"
          d="M12 16.8 6.7 19.3l.7-5.8L3.4 9.2l5.7-1.1L12 3l2.8 5.1L20.6 9.2l-4 4.3.7 5.8z"
        />
      </defs>
      <g className={styles.actionStarredStroke} fill="none">
        <use xlinkHref="#star" />
      </g>
      <g className={styles.actionStarredFill}>
        <use xlinkHref="#star" />
      </g>
    </SVG>
  );
};

export const ActionNoteIcon = ({
  width = 24,
  height = 24,
  color = 'primary',
  hoverOpacity = true,
  viewBox = '0 0 24 24',
  strokeWidth = 2,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color, hoverOpacity, viewBox }}
    className={classnames('icon action-note-icon', props.className)}
  >
    <path
      strokeWidth={strokeWidth}
      fill="none"
      strokeLinecap="round"
      d="M2.5 6h6M5.5 9V3M12.94 5h2.81a3.75 3.75 0 0 1 3.75 3.75v7.5A3.75 3.75 0 0 1 15.75 20h-7.5a3.75 3.75 0 0 1-3.75-3.75v-2.81h0"
    />
  </SVG>
);

export const ActionArchiveIcon = ({
  width = 24,
  height = 24,
  color = 'primary',
  hoverOpacity = true,
  ...props
}: SVGProps) => (
  <TrashIcon
    width={width}
    height={height}
    hoverOpacity={hoverOpacity}
    color={color}
    {...props}
    strokeWidth="1.4"
    viewBox="-2 -2 20 20"
  />
);

export const ActionRemoveIcon = ({
  width = 24,
  height = 24,
  color = 'primary',
  hoverOpacity = true,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color, hoverOpacity }}
    className={classnames('icon action-remove-icon', props.className)}
    viewBox="0 0 24 24"
  >
    <path strokeWidth="2" fill="none" strokeLinecap="round" d="M7 6.4L18.2 17.5m-11.2 0L18.2 6.4" />
  </SVG>
);

export const ActionPlusIcon = ({
  width = 24,
  height = 24,
  color = 'primary',
  hoverOpacity = true,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color, hoverOpacity }}
    className={classnames('icon action-remove-icon', props.className)}
    viewBox="0 0 24 24"
  >
    <path
      strokeWidth={2}
      fill="none"
      strokeLinecap="round"
      d="m11.964 4.115.07 15.768m-7.919-7.92 15.768.072"
    />
  </SVG>
);

export const ActionCheckIcon = ({
  width = 24,
  height = 24,
  color = 'primary',
  hoverOpacity = true,
  strokeWidth = 1.8,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color, hoverOpacity }}
    className={classnames('icon action-check-icon', props.className)}
    viewBox="0 0 24 24"
  >
    <g fill="none">
      <path
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m20 6-2.514 2.998L9.102 19l-4.376-3.611-.726-.6"
      />
    </g>
  </SVG>
);

export const AddNoteIcon = ({ width = 17, height = 17, color = 'primary', ...props }: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon add-note-icon', props.className)}
    viewBox="0 0 17 17"
  >
    <path
      stroke="none"
      d="M13.1 3.5a3.2 3.2 0 013.2 3l.0.2v6.4a3.2 3.2 0 01-3.0 3.2l-.182.0H6.7a3.2 3.2 0 01-3.2-3.0l-.0-.2v-2.3a.8.8 0 011.6-.1l.0.1v2.3c0 .8.6 1.5 1.5 1.6l.1.0h6.4c.8 0 1.5-.6 1.6-1.5l.0-.1V6.7c0-.8-.6-1.5-1.5-1.6l-.1-.0h-2.3a.8.8 0 01-.1-1.6l.1-.0h2.3zm-6.0 0a.8.8 0 01.1 1.6l-.1.0H1.5a.8.8 0 01-.1-1.6l.1-.0h5.6M4.3.7a.8.8 0 01.8.7l.0.1v5.6a.8.8 0 01-1.6.1l-.0-.1V1.5a.8.8 0 01.8-.8z"
    />
  </SVG>
);

export const AddPersonIcon = ({
  width = 20,
  height = 18,
  color = 'primary',
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon add-person-icon', props.className)}
    viewBox="0 0 20 18"
  >
    <path
      stroke="none"
      d="M7.2 11.1c-2.6 0-6.4 3.2-6.4 4.562 0 1.2 2.3 1.4 6.4 1.4l1-0c3.5-0 5.4-.3 5.4-1.4 0-1.4-3.7-4.6-6.4-4.6zm0 1.5c1 0 2.2.6 3.4 1.6l.3.2c.3.2.5.5.7.7l.2.2-.1.0c-.1.0-.2.0-.4.1l-.2.0c-1.0.1-2.4.2-3.8.2a38.2 38.2 0 01-3.5-.1l-.5-.1a12.2 12.2 0 01-.4-.1l-.1-.0-.0.0a9.6 9.6 0 011.2-1.1c1.1-1 2.3-1.6 3.4-1.6zm.1-9.3a3.3 3.3 0 110 6.6 3.3 3.3 0 010-6.6zm0 1.5a1.8 1.8 0 100 3.6 1.8 1.8 0 000-3.6zM18.3 3.4a.7.7 0 01.1 1.4l-.1.0H13a.7.7 0 01-.1-1.4L13 3.4h5.3M15.7.7c.4 0 .7.3.7.6l.0.1v5.3a.7.7 0 01-1.4.1l-.0-.1V1.4a.7.7 0 01.7-.7z"
    />
  </SVG>
);

export const CameraIcon = ({
  width = 20,
  color = 'primary',
  strokeWidth = 2,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, color }}
    className={classnames('icon camera-icon', props.className)}
    viewBox="-1 -1 24 20"
  >
    <g strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <path
        fill="none"
        d="M22 16a2 2 0 01-2 2H2a2 2 0 01-2-2V5a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2v11z"
      />
      <circle fill="none" cx="11" cy="10" r="4" />
    </g>
  </SVG>
);

export const CircledArrowIcon = ({
  width = 12,
  height = 12,
  color = 'textPrimary',
  strokeWidth = 1.2,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon circled-arrow-icon', props.className)}
    viewBox="0 0 14 14"
  >
    <path
      fill="none"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M7.5 9.6 10.2 7 7.5 4.4M3.4 7h5.5"
    />
    <IconCircle fill="none" strokeWidth={strokeWidth} />
  </SVG>
);

export const CircledCheckIcon = ({
  width = 15,
  height = 15,
  color = 'textPrimary',
  strokeWidth = 1.33,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon circled-check-icon', props.className)}
    viewBox="0 0 16 16"
  >
    <g fill="none" strokeWidth={strokeWidth}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5.33 8.45 7 10.22l3.66-4.44" />
      <circle cx="8" cy="8" r="6" />
    </g>
  </SVG>
);


// <svg width="16px" height="16px" viewBox="0 0 16 16" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
//     <defs>
//         <linearGradient x1="-10.6611059%" y1="-9.37656788%" x2="106.064095%" y2="108.166616%" id="linearGradient-7zrtu634p5-1">
//             <stop stop-color="#8FC660" offset="0%"></stop>
//             <stop stop-color="#569B94" offset="100%"></stop>
//         </linearGradient>
//         <linearGradient x1="-74.7517717%" y1="-60.8363354%" x2="180.506995%" y2="161.409618%" id="linearGradient-7zrtu634p5-2">
//             <stop stop-color="#8FC660" offset="0%"></stop>
//             <stop stop-color="#569B94" offset="100%"></stop>
//         </linearGradient>
//     </defs>
//     <g id="📐-mockup" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
//         <g id="modals" transform="translate(-2036, -206)" fill-rule="nonzero" stroke-width="1.33">
//             <g id="modal-copy-13" transform="translate(1720, 80)">
//                 <g id="summary" transform="translate(16, 110)">
//                     <g id="01" transform="translate(12, 16)">
//                         <g id="icn-/-archive-dark-gradient" transform="translate(288, 0)">
//                             <path d="M14,8 C14,11.3137079 11.3137079,14 8,14 C4.68629127,14 2,11.3137079 2,8 C2,4.68629127 4.68629127,2 8,2 C11.3137079,2 14,4.68629127 14,8 Z" id="Shape" stroke="url(#linearGradient-7zrtu634p5-1)"></path>
//                             <g id="Checkmark" transform="translate(5.3333, 5.8333)" stroke="url(#linearGradient-7zrtu634p5-2)" stroke-linecap="round" stroke-linejoin="round">
//                                 <g id="Shape">
//                                     <path d="M0,2.6666669 L1.67320239,4.44444466 L5.33333333,0"></path>
//                                 </g>
//                             </g>
//                         </g>
//                     </g>
//                 </g>
//             </g>
//         </g>
//     </g>
// </svg>


export const GreenCheckMarkIcon = ({
  width = 15,
  height = 15,
  color = 'textPrimary',
  strokeWidth = 1.33,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon circled-check-icon', props.className)}
    viewBox="0 0 16 16"
  >
    <defs>
      {/* <linearGradient id="greenCheckMarkLG">

      </linearGradient> */}


      {/* <linearGradient x1="-10.6611059%" y1="-9.37656788%" x2="106.064095%" y2="108.166616%" id="greenCheckMarkLG">
            <stop stopColor="#8FC660" offset="0%"></stop>
            <stop stopColor="#569B94" offset="100%"></stop>
        </linearGradient> */}
        <linearGradient x1="-74.7517717%" y1="-60.8363354%" x2="180.506995%" y2="161.409618%" id="greenCheckMarkLG">
            <stop stopColor="#8FC660" offset="0%"></stop>
            <stop stopColor="#569B94" offset="100%"></stop>
        </linearGradient>

      {/* <linearGradient x1="50%" y1="0%" x2="50%" y2="100%" id="linearGradient-1">
        <stop stopColor="#F9C987" offset="0%"></stop>
        <stop stopColor="#D89148" offset="100%"></stop>
      </linearGradient> */}
    </defs>
    <g fill="none" strokeWidth={strokeWidth} stroke="url(#greenCheckMarkLG)">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5.33 8.45 7 10.22l3.66-4.44" />
      <circle cx="8" cy="8" r="6" />
    </g>
  </SVG>
);

export const CircledIIcon = ({
  width = 14,
  height = 14,
  color = 'primary',
  strokeWidth = 1.2,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon circled-i-icon', props.className)}
    viewBox="0 0 14 14"
  >
    <path strokeWidth="1.4" strokeLinecap="round" d="M7 6.7v3.1" />
    <circle stroke="none" cx="7" cy="4.25" r="1" />
    <IconCircle fill="none" strokeWidth={strokeWidth} />
  </SVG>
);

export const CircledExclamationPointIcon = ({
  width = 24,
  height = 24,
  color = 'textPrimary',
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon circled-exclamation-point-icon', props.className)}
    viewBox="0 0 16 16"
  >
    <circle cx="8" cy="8" r="6" fill="none" strokeWidth="1.33" />
    <circle cx="8" cy="10.84" r="1" stroke="none" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.33" d="M8 5.02V8.34" />
  </SVG>
);

export const CircledXIcon = ({
  width = 12,
  height = 12,
  color = 'primary',
  strokeWidth = 1.2,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon circled-x-icon', props.className)}
    viewBox="0 0 14 14"
  >
    <path
      fill="none"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      d="M4.3 4.3L9.7 9.7M4.3 9.7L9.7 4.3"
    />
    <IconCircle fill="none" strokeWidth={strokeWidth} />
  </SVG>
);

export const CircleMagicIcon = ({
  width = 18,
  color = 'primary',
  strokeWidth = 1.2,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, color }}
    className={classnames('icon circle-magic-icon', props.className)}
    viewBox="0 0 14 20"
  >
    {/* Magic sparkles graphic path */}
    <path
      stroke="none"
      transform="scale(0.74)"
      d="M12.8 3.8c-.1 0-.2-.2-.2-.2-.2-1.3-.2-1.3-1.6-1.6-.1-.0-.2-.1-.2-.2 0-.106.1-.2.2-.2 1.3-.2 1.3-.3 1.6-1.6.0-.1.1-.2.2-.2.1 0 .2.1.2.2.2 1.3.2 1.3 1.6 1.6.1.0.2.1.2.2 0 .1-.1.2-.2.2-1.3.3-1.3.3-1.6 1.6-.2.1-.1.2-.2.2zm2.8 6.8a.3.3 0 01-.3-.3c-.3-2.05-.3-2.1-2.5-2.5-.2-.0-.3-.1-.3-.3a.3.3 0 01.3-.3c2.1-.3 2.2-.4 2.5-2.4.0-.2.1-.3.3-.3.2 0 .3.1.3.3.2 2.0.3 2 2.4 2.4.2.0.3.1.3.3 0 .2-.1.3-.3.3-2.1.3-2.2.4-2.4 2.4-.0.2-.1.3-.3.3zM9.5 18c-.2 0-.3-.1-.3-.3-.4-3.1-.8-3.5-3.9-3.9-.2-.0-.3-.2-.3-.3 0-.2.1-.3.3-.3 3-.4 3.4-.8 3.9-3.9.0-.2.2-.3.3-.3s.3.1.3.3c.4 3.1.8 3.5 3.9 3.9.2.0.3.2.3.3 0 .2-.1.3-.3.3-2.99.4-3.4.8-3.9 3.9-.0.2-.2.3-.3.3z"
    />
    <PartialCircle fill="none" strokeWidth={strokeWidth} transform="translate(0, 2.9)" />
  </SVG>
);

export const CirclePenIcon = ({
  width = 17,
  height = 17,
  color = 'primary',
  strokeWidth = 1.2,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon circle-pen-icon', props.className)}
    viewBox="0 0 14 14"
  >
    {/* Pen graphic path */}
    <path
      strokeWidth={strokeWidth}
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M5.6 8.7l0.3 -1.9l5 -5a1 1 0 0 1 1.5 1.5l-5 5z"
    />
    <PartialCircle fill="none" strokeWidth={strokeWidth} />
  </SVG>
);

export const SelectAllIcon = forwardRef<SVGSVGElement, SVGProps>(
  (
    { width = 18, height = 18, color = 'primary', hoverOpacity = true, ...props }: SVGProps,
    ref,
  ) => (
    <SVG
      {...{ ...props, width, height, color, hoverOpacity }}
      ref={ref as RefObject<SVGSVGElement>}
      className={classnames('icon select-all-icon', props.className)}
      viewBox="0 0 14 14"
    >
      <g strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
        <path
          fill="none"
          d="M6 1h-2a2.8 2.8 0 0 0 -3 3v6a2.8 2.8 0 0 0 3 3h6a2.8 2.8 0 0 0 3 -3v-2"
        />
        <path fill="none" d="M4 6.3l2.8 2.6l5.7 -6.7" />
      </g>
    </SVG>
  ),
);

export const SortIcon = ({ width = 20, height = 18, color = 'primary', ...props }: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon sort-icon', props.className)}
    viewBox="2.25 3 20 18"
  >
    <path
      fill="none"
      strokeLinecap="round"
      strokeWidth="2"
      strokeLinejoin="round"
      d="m21 16-3.5 3.5L14 16M17.5 4.5v15 M3.5 6h9M3.5 11.5h9M3.5 17h5"
    />
  </SVG>
);

export const SortDirectionIcon = ({
  width = 16,
  height = 16,
  hover = true,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, hover }}
    className={classnames('icon sort-direction-icon', props.className)}
    viewBox="0 0 16 16"
  >
    <path
      d="m11 6.5-3-3-3 3 3-3v9.5"
      fill="none"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </SVG>
);

export const FilterIcon = ({ width = 13, height = 8, strokeWidth = 1.33, ...props }: SVGProps) => (
  <SVG
    {...{ ...props, width, height }}
    className={classnames('icon filter-icon', props.className)}
    viewBox="0 0 13 8"
  >
    <path
      fill="none"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      d="M0.7 0.7H12.3M2.7 4H10.3M4.7 7.3H8.3"
    />
  </SVG>
);

export const ChevronRightIcon = ({
  width = 8,
  height = 14,
  color = 'primary',
  strokeWidth = 1.6,
  ...props
}: SVGProps) => (
  <SVG
    className={classnames('icon chevron-icon chevron-right-icon', props.className)}
    viewBox="0 0 8 14"
    {...{ ...props, width, height, color }}
  >
    <Chevron fill="none" strokeWidth={strokeWidth} />
  </SVG>
);

export const ChevronLeftIcon = ({
  width = 8,
  height = 14,
  color = 'primary',
  strokeWidth = 1.6,
  hover = true,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color, hover }}
    className={classnames('icon chevron-icon chevron-left-icon', props.className)}
    viewBox="0 0 8 14"
  >
    <g transform="rotate(180, 4, 7)">
      <Chevron fill="none" strokeWidth={strokeWidth} />
    </g>
  </SVG>
);

export const ChevronDownIcon = ({
  width = 10,
  height = 10,
  color = 'textSecondary',
  strokeWidth = 2.1,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon chevron-icon chevron-down-icon', props.className)}
    viewBox="0 0 8 14"
  >
    <g transform="rotate(90, 4, 7)">
      <Chevron fill="none" strokeWidth={strokeWidth} />
    </g>
  </SVG>
);

export const ChevronUpDownIcon = ({
  width = 8,
  height = 14,
  color = 'textSecondary',
  strokeWidth = 3,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon chevron-icon chevron-down-icon', props.className)}
    viewBox="-8 0 24 14"
  >
    <g transform="rotate(-90, 4, 7) translate(7, 0)">
      <Chevron fill="none" strokeWidth={strokeWidth} />
    </g>
    <g transform="rotate(90, 4, 7) translate(7, 0)">
      <Chevron fill="none" strokeWidth={strokeWidth} />
    </g>
  </SVG>
);

export const ClayUserIcon = () => (
  <svg className="badge" viewBox="0 0 14 14" width="14" height="14">
    <defs>
      <linearGradient x1="50%" y1="0%" x2="50%" y2="100%" id="linearGradient-1">
        <stop stopColor="#F9C987" offset="0%"></stop>
        <stop stopColor="#D89148" offset="100%"></stop>
      </linearGradient>
      <path
        id="path-2"
        d="M10,0 C12.209139,-4.05812251e-16 14,1.790861 14,4 L14,10 C14,12.209139 12.209139,14 10,14 L4,14 C1.790861,14 2.705415e-16,12.209139 0,10 L0,4 C-2.705415e-16,1.790861 1.790861,4.05812251e-16 4,0 L10,0 Z M7,4.33333333 C6.37337497,4.33333333 5.85834794,4.82435188 5.7993776,5.45240673 C5.19990946,5.31596184 4.58633497,5.66759116 4.39269719,6.28002417 C4.1990594,6.89245699 4.49432957,7.54755239 5.05735203,7.79926688 C4.7458308,8.34299419 4.88164791,9.0513314 5.3885981,9.42983577 C5.89554852,9.80834013 6.59306275,9.72219297 7,9.24970604 C7.40693725,9.72219297 8.10445148,9.80834013 8.6114019,9.42983577 C9.11835209,9.0513314 9.2541692,8.34299419 8.94264797,7.79926688 C9.50567043,7.54755239 9.8009406,6.89245699 9.60730281,6.28002417 C9.41366503,5.66759116 8.80009054,5.31596184 8.2006224,5.45240673 C8.14165206,4.82435188 7.62662503,4.33333333 7,4.33333333 Z"
      ></path>
      <path
        id="path-3"
        d="M4,0 C6.209139,0 8,1.790861 8,4 C8,6.209139 6.209139,8 4,8 C1.790861,8 0,6.209139 0,4 C0,1.790861 1.790861,0 4,0 Z M4,1.33333333 C3.37337497,1.33333333 2.85834794,1.82435188 2.7993776,2.45240673 C2.19990946,2.31596184 1.58633497,2.66759116 1.39269719,3.28002417 C1.1990594,3.89245699 1.49432957,4.54755239 2.05735203,4.79926688 C1.7458308,5.34299419 1.88164791,6.0513314 2.3885981,6.42983577 C2.89554852,6.80834013 3.59306275,6.72219297 4,6.24970604 C4.40693725,6.72219297 5.10445148,6.80834013 5.6114019,6.42983577 C6.11835209,6.0513314 6.2541692,5.34299419 5.94264797,4.79926688 C6.50567043,4.54755239 6.8009406,3.89245699 6.60730281,3.28002417 C6.41366503,2.66759116 5.80009054,2.31596184 5.2006224,2.45240673 C5.14165206,1.82435188 4.62662503,1.33333333 4,1.33333333 Z"
      ></path>
      <filter
        x="-50.0%"
        y="-50.0%"
        width="200.0%"
        height="200.0%"
        filterUnits="objectBoundingBox"
        id="filter-4"
      >
        <feGaussianBlur stdDeviation="1.33" in="SourceGraphic"></feGaussianBlur>
      </filter>
    </defs>
    <g stroke="none" strokeWidth="1" fillRule="evenodd">
      <use stroke="none" fill="#FCD6A4" xlinkHref="#path-2" opacity="0.16" fillRule="nonzero"></use>
      <g transform="translate(3.000000, 3.000000)">
        <use stroke="none" fill="#FCD6A4" xlinkHref="#path-3" filter="url(#filter-4)"></use>
        <use
          stroke="none"
          xlinkHref="#path-3"
          fill="url(#linearGradient-1)"
          fillRule="nonzero"
        ></use>
      </g>
    </g>
  </svg>
);

export const ClayUserWithTextIcon = () => {
  const id = useMemo(() => Math.random(), []);
  const grad1 = `ClayUserGrad1_${id}`;
  const grad2 = `ClayUserGrad2_${id}`;
  const grad3 = `ClayUserGrad3_${id}`;
  const filter = `ClayUserFilter_${id}`;

  return (
    <svg className="badge" viewBox="0 0 46 14" width="46" height="14">
      <defs>
        <linearGradient x1="50%" y1="0%" x2="50%" y2="100%" id={grad1}>
          <stop stopColor="#FFBD27" offset="0%" />
          <stop stopColor="#FFBD29" offset="100%" />
        </linearGradient>
        <linearGradient x1="50%" y1="0%" x2="50%" y2="100%" id={grad2}>
          <stop stopColor="#F9C987" offset="0%" />
          <stop stopColor="#D89148" offset="100%" />
        </linearGradient>
        <linearGradient x1="50%" y1="0%" x2="50%" y2="100%" id={grad3}>
          <stop stopColor="#F9C987" offset="0%" />
          <stop stopColor="#D89148" offset="100%" />
        </linearGradient>
        <filter
          x="-50%"
          y="-50%"
          width="200%"
          height="200%"
          filterUnits="objectBoundingBox"
          id={filter}
        >
          <feGaussianBlur stdDeviation="1.167" in="SourceGraphic" />
        </filter>
      </defs>
      <g fill="none" fillRule="evenodd">
        <rect
          fillOpacity=".1"
          fill={`url(#${grad1})`}
          opacity=".716"
          width="46"
          height="15"
          rx="4"
        />
        <g transform="translate(4 1)">
          <circle fill={`url(#${grad2})`} cx="3.5" cy="6.5" r="3.5" />
          <circle
            fill={`url(#${grad2})`}
            opacity=".419"
            filter={`url(#${filter})`}
            cx="3.5"
            cy="6.5"
            r="3.5"
          />
          <path
            className={styles.themedFill}
            opacity=".947"
            strokeOpacity=".341"
            stroke="#FFF"
            strokeWidth=".1"
            d="M3.5 4.117c.286 0 .547.111.743.294.185.173.313.409.35.675a1.08 1.08 0 01.735.135c.23.135.413.355.501.634.088.279.066.567-.043.815a1.12 1.12 0 01-.517.552 1.16 1.16 0 01-.33 1.444A1.08 1.08 0 013.5 8.543a1.08 1.08 0 01-1.44.123 1.134 1.134 0 01-.432-.685 1.16 1.16 0 01.103-.76 1.12 1.12 0 01-.517-.551 1.158 1.158 0 01-.043-.815c.088-.28.272-.499.501-.634a1.08 1.08 0 01.734-.135c.038-.266.166-.502.351-.675.196-.183.457-.294.743-.294z"
          />
          <path
            fill={`url(#${grad3})`}
            d="M14.193 10.132c1.47 0 2.544-.918 2.544-2.207v-.073h-1.079c-.014.78-.59 1.328-1.46 1.328-1.02 0-1.596-.747-1.596-2.085v-.82c0-1.358.576-2.115 1.596-2.115.87 0 1.446.581 1.46 1.416h1.08v-.073c0-1.353-1.07-2.3-2.545-2.3-1.718 0-2.71 1.157-2.71 3.071v.816c0 1.9.992 3.042 2.71 3.042zM22.972 10v-.962h-2.94V3.335h-1.108V10h4.048zm2.655 0l.596-1.846h2.339l.59 1.846h1.172l-2.29-6.665h-1.24L24.499 10h1.128zm2.686-2.695h-1.841l.903-2.818h.044l.893 2.818zM34.21 10V7.295l2.231-3.96h-1.215l-1.538 2.886h-.04l-1.533-2.886H30.88l2.231 3.98V10h1.099z"
          />
        </g>
      </g>
    </svg>
  );
};

export const NewContactIcon = ({ className }) => (
  <svg className={className} width="14" height="14" viewBox="0 0 14 14">
    <g className={styles.badge} fillRule="evenodd" stroke="none" strokeWidth={0}>
      <rect width="14" height="14" rx="4" />
      <circle cx="7" cy="7" r="1.6" />
      <circle cx="7" cy="2.8" r="0.6" />
      <circle cx="7" cy="11.2" r="0.6" />
      <circle cx="4.3" cy="4.4" r="0.6" />
      <circle cx="9.7" cy="4.4" r="0.6" />
      <circle cx="4.3" cy="9.8" r="0.6" />
      <circle cx="9.7" cy="9.8" r="0.6" />
      <circle cx="2.8" cy="7" r="0.6" />
      <circle cx="11.2" cy="7" r="0.6" />
    </g>
  </svg>
);

export const NewContactWithTextIcon = ({ className }) => (
  <svg className={classnames(className, 'badge')} width="45" height="14" viewBox="0 0 45 14">
    <defs>
      <filter
        x="-36.4%"
        y="-36.3%"
        width="172.8%"
        height="172.5%"
        filterUnits="objectBoundingBox"
        id="b"
      >
        <feGaussianBlur stdDeviation="1" in="SourceGraphic" />
      </filter>
      <g id="circles">
        <circle cx="9.2" cy="6.8" r="1.6" />
        <circle cx="9.2" cy="2.6" r="0.6" />
        <circle cx="9.2" cy="11" r="0.6" />
        <circle cx="6.5" cy="4.2" r="0.6" />
        <circle cx="11.9" cy="4.2" r="0.6" />
        <circle cx="6.5" cy="9.6" r="0.6" />
        <circle cx="11.9" cy="9.6" r="0.6" />
        <circle cx="5" cy="6.8" r="0.6" />
        <circle cx="13.4" cy="6.8" r="0.6" />
      </g>
    </defs>
    <g className={styles.badge} fillRule="evenodd">
      <rect stroke="none" x="-.152" width="45" height="14" rx="4" />
      <path
        stroke="none"
        opacity=".705"
        d="M19.87 10.3V5.5h.03l3.159 4.8h.967V3.635h-1.04V8.42h-.034l-3.15-4.785h-.976V10.3h1.044zm10.815 0v-.933h-3.057V7.361h2.881v-.894h-2.88v-1.9h3.056v-.932h-4.16V10.3h4.16zm4.384 0l1.313-4.897h.034L37.73 10.3h1.006l1.797-6.665h-1.197l-1.142 4.966h-.044l-1.24-4.966h-1.02l-1.241 4.966h-.04l-1.152-4.966h-1.191l1.792 6.665h1.01z"
      />
      <use href="#circles" opacity=".314" filter="url(#b)" strokeWidth="1.08" />
      <use href="#circles" stroke="none" />
    </g>
  </svg>
);

export const ClearProfileIcon = ({
  width = 12,
  height = 10,
  color = 'primary',
  strokeWidth = 1.35,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon clear-profile-icon', props.className)}
    viewBox="0 0.2 12 10"
  >
    <path
      stroke="none"
      d="M4.2 6.2C2.5 6.2.1 8.2.1 9.1c0 .5.6.8 1.7.9l.4.0.2.0.5.0.8.0.8.0.8-.0.5-.0.2-.0.4-.0.4-.0c1.1-.1 1.6-.4 1.6-.9 0-.9-2.4-2.9-4-2.9zm0 1c.6 0 1.4.4 2.1.97l.2.2c.1.1.3.3.4.4l.1.1-.1.0-.2.0c-.2.0-.5.0-.7.1l-.6.0-.6.0-.3.0-.2.0c-.3 0-.7-.0-1-.0l-.6-.0a17.7 17.7 0 01-.5-.0l-.4-.0-.2-.0.1-.1c.2-.2.4-.4.6-.6.7-.6 1.4-1 2.1-1zm0-5.9a2.1 2.06 0 110 4.13 2.06 2.06 0 010-4.13zm0 1a1 1 0 100 2 1 1 0 000-2zM9.1.4l.1.1.9.9.9-.9a.5.5 0 01.8.7l-.1.1-.9.9.9.9a.5.5 0 01-.7.8l-.1-.1-.9-.9-.9.9a.5.5 0 01-.8-.7l.1-.1.9-.9-.9-.9A.5.5 0 019.1.4z"
    />
  </SVG>
);

export const ClockIcon = ({
  width = 16,
  height = 16,
  color = 'primary',
  strokeWidth = 1.33,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon clock-icon', props.className)}
    viewBox="0 0 16 16"
  >
    <g fill="none" strokeWidth={strokeWidth}>
      <path d="M14 8A6 6 0 1 1 2 8a6 6 0 0 1 12 0Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M7.67 4.67V9h3.5" />
    </g>
  </SVG>
);

export const ReminderAlarmClockIcon = ({
  width = 24,
  height = 24,
  color = 'primary',
  strokeWidth = 2,
  colorized = false,
  ...props
}: SVGProps & { colorized?: boolean }) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon reminder-alarm-clock-icon', props.className)}
    viewBox="0 0 24 24"
  >
    <defs>
      <linearGradient id="reminderAlarmClockIconLG" x1="50%" x2="50%" y1="0%" y2="100%">
        <stop offset="0%" stopColor="#35B5FF" />
        <stop offset="100%" stopColor="#4A90E2" />
      </linearGradient>
    </defs>
    <g
      stroke={colorized ? 'url(#reminderAlarmClockIconLG)' : undefined}
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={strokeWidth}
    >
      <circle cx="12" cy="13.5" r="7" />
      <path d="M11.75 10.25v4l1.75 2" />
      <path d="M7.4 3.5C5.63 4.32 4.11 5.6 3 7.17m18 .03a11.05 11.05 0 0 0-4.46-3.7" />
    </g>
  </SVG>
);

export const LockIcon = ({
  width = 24,
  height = 24,
  color = 'primary',
  strokeWidth = 1.17,
  viewBox = '0 0 24 24',
  ...props
}: SVGProps) => (
  <SVG
    viewBox={viewBox}
    {...{ ...props, width, height, color }}
    className={classnames('icon lock-icon', props.className)}
  >
    <g strokeWidth={strokeWidth}>
      <circle cx="12" cy="13.5" r="0.9" stroke="none" />
      <path fill="none" d="M9.4 10.5 C 9.6 6.7 14.4 6.7 14.6 10.5" />
      <rect x="8.5" y="10.5" width="7" height="5.8" rx="1.5" fill="none" />
    </g>
  </SVG>
);

export const GlobeIcon = ({
  width = 24,
  height = 24,
  color = 'primary',
  strokeWidth = 1.17,
  ...props
}: SVGProps) => (
  <SVG
    viewBox="0 0 24 24"
    {...{ ...props, width, height, color }}
    className={classnames('icon globe-icon', props.className)}
  >
    <g strokeWidth={strokeWidth}>
      <circle cx="12" cy="12" r="5.85" fill="none" />
      <path
        d="M6.1 12H17.8 M12 6.2C13.5 7.8 14.3 9.8 14.3 12C14.3 14.2 13.5 16.2 12 17.8C10.5 16.2 9.7 14.2 9.7 12C9.7 9.8 10.5 7.8 12 6.2V6.2Z"
        fill="none"
      />
    </g>
  </SVG>
);

export const CommandIcon = ({ width = 14, height = 14, color = 'primary', ...props }: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon command-icon', props.className)}
    viewBox="0 0 12 13"
  >
    <path
      stroke="none"
      d="M4.69907227,6.91040039 L6.91040039,6.91040039 L6.91040039,4.69907227 L4.69907227,4.69907227 L4.69907227,6.91040039 Z M4.69907227,8.01606445 L4.69907227,9.28608398 C4.69907227,9.92358398 4.46872559,10.4701904 4.00803223,10.9259033 C3.54733887,11.3816162 2.99326172,11.6094727 2.34580078,11.6094727 C1.69833984,11.6094727 1.14550781,11.3803711 0.687304687,10.922168 C0.229101562,10.4639648 0,9.90864258 0,9.25620117 C0,8.61870117 0.229101562,8.06835938 0.687304687,7.60517578 C1.14550781,7.14199219 1.69086914,6.91040039 2.32338867,6.91040039 L3.5934082,6.91040039 L3.5934082,4.69907227 L2.32338867,4.69907227 C1.69086914,4.69907227 1.14550781,4.46748047 0.687304687,4.00429688 C0.229101562,3.54111328 0,2.98828125 0,2.34580078 C0,1.69833984 0.229101562,1.14550781 0.687304687,0.687304688 C1.14550781,0.229101563 1.69833984,0 2.34580078,0 C2.99326172,0 3.54733887,0.227856445 4.00803223,0.683569336 C4.46872559,1.13928223 4.69907227,1.68588867 4.69907227,2.32338867 L4.69907227,3.5934082 L6.91040039,3.5934082 L6.91040039,2.32338867 C6.91040039,1.68588867 7.14074707,1.13928223 7.60144043,0.683569336 C8.06213379,0.227856445 8.61621094,0 9.26367188,0 C9.91611328,0 10.4701904,0.229101563 10.9259033,0.687304688 C11.3816162,1.14550781 11.6094727,1.69833984 11.6094727,2.34580078 C11.6094727,2.98828125 11.3816162,3.54111328 10.9259033,4.00429688 C10.4701904,4.46748047 9.92358398,4.69907227 9.28608398,4.69907227 L8.01606445,4.69907227 L8.01606445,6.91040039 L9.28608398,6.91040039 C9.92358398,6.91040039 10.4701904,7.14199219 10.9259033,7.60517578 C11.3816162,8.06835938 11.6094727,8.61870117 11.6094727,9.25620117 C11.6094727,9.90864258 11.3816162,10.4639648 10.9259033,10.922168 C10.4701904,11.3803711 9.91611328,11.6094727 9.26367188,11.6094727 C8.61621094,11.6094727 8.06213379,11.3816162 7.60144043,10.9259033 C7.14074707,10.4701904 6.91040039,9.92358398 6.91040039,9.28608398 L6.91040039,8.01606445 L4.69907227,8.01606445 Z M3.5934082,3.5934082 L3.5934082,2.34580078 C3.5934082,2.00214844 3.47138672,1.7095459 3.22734375,1.46799316 C2.98330078,1.22644043 2.68945312,1.10566406 2.34580078,1.10566406 C2.00712891,1.10566406 1.71577148,1.22644043 1.47172852,1.46799316 C1.22768555,1.7095459 1.10566406,2.00214844 1.10566406,2.34580078 C1.10566406,2.68945313 1.22768555,2.98330078 1.47172852,3.22734375 C1.71577148,3.47138672 2.00961914,3.5934082 2.35327148,3.5934082 L3.5934082,3.5934082 Z M3.5934082,8.01606445 L2.35327148,8.01606445 C2.00961914,8.01606445 1.71577148,8.13808594 1.47172852,8.38212891 C1.22768555,8.62617188 1.10566406,8.9175293 1.10566406,9.25620117 C1.10566406,9.59985352 1.22768555,9.89370117 1.47172852,10.1377441 C1.71577148,10.3817871 2.00961914,10.5038086 2.35327148,10.5038086 C2.69194336,10.5038086 2.98330078,10.3817871 3.22734375,10.1377441 C3.47138672,9.89370117 3.5934082,9.60234375 3.5934082,9.26367188 L3.5934082,8.01606445 Z M8.01606445,3.5934082 L9.26367188,3.5934082 C9.60234375,3.5934082 9.89370117,3.47138672 10.1377441,3.22734375 C10.3817871,2.98330078 10.5038086,2.68945313 10.5038086,2.34580078 C10.5038086,2.00214844 10.3830322,1.7095459 10.1414795,1.46799316 C9.89992676,1.22644043 9.60732422,1.10566406 9.26367188,1.10566406 C8.92001953,1.10566406 8.62617187,1.22644043 8.38212891,1.46799316 C8.13808594,1.7095459 8.01606445,2.00214844 8.01606445,2.34580078 L8.01606445,3.5934082 Z M8.01606445,8.01606445 L8.01606445,9.26367188 C8.01606445,9.60234375 8.13808594,9.89370117 8.38212891,10.1377441 C8.62617187,10.3817871 8.92001953,10.5038086 9.26367188,10.5038086 C9.60732422,10.5038086 9.89992676,10.3817871 10.1414795,10.1377441 C10.3830322,9.89370117 10.5038086,9.59985352 10.5038086,9.25620117 C10.5038086,8.9175293 10.3817871,8.62617188 10.1377441,8.38212891 C9.89370117,8.13808594 9.60234375,8.01606445 9.26367188,8.01606445 L8.01606445,8.01606445 Z"
    />
  </SVG>
);

export const EnclosedCircleIcon = ({
  width = 14,
  height = 14,
  color = 'primary',
  strokeWidth = 1.35,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon enclosed-circle-icon', props.className)}
    viewBox="0 0 14 14"
  >
    <circle stroke="none" cx="7" cy="7" r="2" />
    <circle
      fill="none"
      strokeWidth={strokeWidth}
      cx="7"
      cy="7"
      r="6.2"
      strokeDasharray="9,3"
      strokeLinecap="round"
      transform="rotate(26,7,7)"
    />
  </SVG>
);

export const EnclosedDotsIcon = ({
  width = 14,
  height = 10,
  color = 'textSecondary',
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon enclosed-dots-icon', props.className)}
    viewBox="0 0 14 10"
  >
    <g fill="none">
      <rect x="0.5" y="0.5" width="13" height="9" rx="3.6" />
      <circle cx="7.5" cy="6" r="0.85" />
      <circle cx="10.7" cy="6" r="0.85" />
      <circle cx="4.3" cy="6" r="0.85" />
    </g>
  </SVG>
);

export const EmailIcon = ({ width = 14, height = 14, ...props }: SVGProps) => {
  const [theme] = useTheme();
  const boxOpacity = theme === 'light' ? 0.1 : 0.3;
  const stroke = theme === 'light' ? '#E4D2F4' : '#362140';

  return (
    <SVG
      {...{ ...props, width, height }}
      className={classnames('icon email-icon', props.className)}
      viewBox="0 0 14 14"
    >
      <defs>
        <linearGradient x1="50%" y1="-57%" x2="50%" y2="100%" id="emailIconLG">
          <stop stopColor="#C987E5" offset="0%" />
          <stop stopColor="#984FC6" offset="100%" />
        </linearGradient>
      </defs>
      <rect stroke="none" fill="#C987E5" opacity={boxOpacity} width="14" height="14" rx="4" />
      <rect stroke="none" fill="url(#emailIconLG)" x="2" y="3.5" width="10" height="7" rx=".44" />
      <path
        stroke={stroke}
        fill="none"
        d="M11.4 4L7 7.8 2.6 4m0 5.8 l3.2-2.8m2.4 0l3.2 2.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="0.8"
      />
    </SVG>
  );
};

export const EnvelopeIcon = ({
  width = 24,
  height = 19,
  strokeWidth = 2,
  color = 'primary',
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height }}
    className={classnames('icon envelope-icon', props.className)}
    viewBox="0 3 24 19"
  >
    <g strokeWidth={strokeWidth} strokeLinejoin="round">
      <path fill="none" d="M22 6.5L12 13.5L2 6.5" />
      <rect fill="none" x="2" y="4.5" width="20" height="16" rx="2" />
    </g>
  </SVG>
);

export const EmailSourceIcon = ({
  width = 16,
  height = 16,
  color = 'textPrimary',
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon email-source-icon', props.className)}
    viewBox="0 0 16 16"
  >
    <g fill="none" strokeWidth="1.33">
      <rect width="12" height="10" x="2" y="3" rx="2" />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m13.33 4.67-4.9 4.28a.67.67 0 0 1-.87 0l-4.9-4.28h0"
      />
    </g>
  </SVG>
);

export const PhoneIcon = ({
  width = 16,
  height = 16,
  strokeWidth = 1.33,
  color = 'primary',
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon phone-icon', props.className)}
    viewBox="0 0 16 16"
  >
    <g strokeWidth={strokeWidth} strokeLinejoin="round">
      <path
        fill="none"
        d="M4.5 2.05a1.35 1.35 0 0 1 1.65.83l.95 2.44c-.3 1.23-.68 1.38-1 1.49l-.46.2c.2.63.6 1.18 1.39 1.96l1.62 1.62.54-.7c.13-.32.39-.6.7-.75.35-.17.73-.2 1.08-.07l2.15.78a1.35 1.35 0 0 1 .83 1.64l-.4 1.35a1.54 1.54 0 0 1-1.3 1.1 9.03 9.03 0 0 1-7.47-2.72 9.03 9.03 0 0 1-2.73-7.47 1.55 1.55 0 0 1 1.1-1.3Z"
      />
    </g>
  </SVG>
);

export const MessagesIcon = ({ width = 14, height = 14, ...props }: SVGProps) => {
  const [theme] = useTheme();
  const boxOpacity = theme === 'light' ? 0.1 : 0.2;

  return (
    <SVG
      {...{ ...props, width, height }}
      className={classnames('icon messages-icon', props.className)}
      viewBox="0 0 14 14"
    >
      <defs>
        <linearGradient x1="216%" y1="125%" x2="0%" y2="50%" id="msgsIconBoxLG">
          <stop stopColor="#FFE550" offset="0%" />
          <stop stopColor="#FFD34F" offset="32.411%" />
          <stop stopColor="#FFB74A" offset="61.953%" />
          <stop stopColor="#FF7E44" offset="100%" />
        </linearGradient>
        <linearGradient x1="234%" y1="398%" x2="0%" y2="50%" id="msgsIconLG">
          <stop stopColor="#FFE550" offset="0%" />
          <stop stopColor="#FFD34F" offset="32%" />
          <stop stopColor="#FFB74A" offset="62%" />
          <stop stopColor="#FF7E44" offset="100%" />
        </linearGradient>
      </defs>
      <rect
        stroke="none"
        fill="url(#msgsIconBoxLG)"
        opacity={boxOpacity}
        width="14"
        height="14"
        rx="4"
      />
      <path
        stroke="none"
        fill="url(#msgsIconLG)"
        d="M2 4.8 C2 3.8 2.8 3 3.8 3h6.5C11.2 3 12 3.8 12 4.8v2.9c0 1 -0.8 1.8 -1.8 1.8H7.4a0.1 0.1 0 0 0 -0.1 0L4.1 11.9c-0.1 0.1 -0.1 0 -0.1 -0.1V9.5a0.1 0.1 0 0 0 -0.1 -0.1h-0.2A1.8 1.8 0 0 12 7.7V4.8z"
      />
    </SVG>
  );
};

export const LinkedInIcon = ({ width = 14, height = 14, ...props }: SVGProps) => {
  const [theme] = useTheme();
  const boxOpacity = theme === 'light' ? 0.4 : 0.3;

  return (
    <SVG
      {...{ ...props, width, height }}
      className={classnames('icon linked-in-icon', props.className)}
      viewBox="0 0 14 14"
    >
      <defs>
        <linearGradient x1="50%" y1="100%" x2="50%" y2="0%" id="linkedInIconLG">
          <stop stopColor="#2386FA" offset="0%"></stop>
          <stop stopColor="#0AA5FF" offset="100%"></stop>
        </linearGradient>
      </defs>
      <rect
        fill="url(#linkedInIconLG)"
        opacity={boxOpacity}
        x="0"
        y="0"
        width="14"
        height="14"
        rx="4"
        stroke="none"
      ></rect>
      <path
        stroke="none"
        transform="translate(3.000000, 3.000000)"
        fill="#fff"
        d="M8,7 L6.2,7 L6.2,4.5 C6.2,3.9 6,3.5 5.3,3.5 C4.9,3.5 4.6,3.8 4.5,4 C4.5,4.1 4.5,4.3 4.5,4.4 L4.5,7 L2.7,7 C2.7,7 2.7,2.6 2.7,2.2 L4.5,2.2 L4.5,3 C4.6,2.7 5.1,2.2 6,2.2 C7.1,2.2 8,2.9 8,4.4 L8,7 L8,7 Z M0.9,1.6 L0.9,1.6 C0.4,1.6 0,1.3 0,0.8 C0,0.4 0.4,0 1,0 C1.5,0 1.9,0.3 1.9,0.8 C1.9,1.3 1.5,1.6 0.9,1.6 L0.9,1.6 L0.9,1.6 Z M0.2,2.2 L1.8,2.2 L1.8,7 L0.2,7 L0.2,2.2 L0.2,2.2 Z"
      ></path>
    </SVG>
  );
};

export const TwitterIcon = ({ width = 14, height = 14, ...props }: SVGProps) => {
  const [theme] = useTheme();
  let fill = '#2686BE';
  let boxOpacity = 0.3;

  if (theme == 'light') {
    fill = '#459FD3';
    boxOpacity = 0.2;
  }

  return (
    <SVG
      {...{ ...props, width, height }}
      className={classnames('icon twitter-icon', props.className)}
      viewBox="0 0 14 14"
    >
      <defs>
        <linearGradient x1="50%" y1="100%" x2="50%" y2="0%" id="TwitterIconLG">
          <stop stopColor="#4A90E2" offset="0%"></stop>
          <stop stopColor="#35B5FF" offset="100%"></stop>
        </linearGradient>
      </defs>
      <rect
        stroke="none"
        opacity={boxOpacity}
        fill="url(#TwitterIconLG)"
        x="0"
        y="0"
        width="14"
        height="14"
        rx="4"
      ></rect>
      <path
        fill={fill}
        stroke="none"
        d="M12,4 C11.6,4.2 11.2,4.3 10.8,4.3 C11.2,4.1 11.6,3.6 11.7,3.2 C11.3,3.4 10.9,3.6 10.4,3.7 C10,3.3 9.5,3 8.9,3 C7.8,3 6.9,3.9 6.9,5.1 C6.9,5.3 6.9,5.4 6.9,5.6 C5.2,5.5 3.7,4.7 2.7,3.4 C2.5,3.7 2.4,4.1 2.4,4.4 C2.4,5.2 2.8,5.8 3.3,6.2 C3,6.2 2.7,6.1 2.4,5.9 L2.4,6 C2.4,7 3.1,7.8 4,8 C3.9,8.1 3.7,8.1 3.5,8.1 C3.4,8.1 3.2,8.1 3.1,8.1 C3.4,8.9 4.1,9.5 5,9.5 C4.3,10.1 3.5,10.4 2.5,10.4 C2.3,10.4 2.2,10.4 2,10.4 C2.9,11 4,11.3 5.1,11.3 C8.9,11.3 11,8.1 11,5.3 C11,5.3 11,5.2 11,5.1 C11.4,4.8 11.7,4.4 12,4"
      ></path>
    </SVG>
  );
};

export const GiftIcon = ({
  width = 12,
  height = 12,
  color = 'textSecondary',
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon gift-icon', props.className)}
    viewBox="0 0 12 12"
  >
    <g fill="none">
      <path d="M10,7 L10,10 C10,10.55 9.55,11 9,11 L3,11 C2.45,11 2,10.55 2,10 L2,7 L2,7 M6,3.5 L3.75,3.5 C3.06,3.5 2.5,2.94 2.5,2.25 C2.5,1.56 3.06,1 3.75,1 C5.5,1 6,3.5 6,3.5 Z M6,3.5 L8.25,3.5 C8.94,3.5 9.5,2.94 9.5,2.25 C9.5,1.56 8.94,1 8.25,1 C6.5,1 6,3.5 6,3.5 Z M6 11v-7.5"></path>
      <rect x="1" y="3.5" width="10" height="3" rx="0.2"></rect>
    </g>
  </SVG>
);

export const ThumbsUpIcon = ({
  width = 14,
  height = 14,
  color = 'textSecondary',
  hover = true,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color, hover }}
    className={classnames('icon thumbs-up-icon', props.className)}
    viewBox="0 0 14 14"
  >
    <Thumb {...props} />
  </SVG>
);

export const ThumbsDownIcon = ({
  width = 14,
  height = 14,
  color = 'textSecondary',
  hover = true,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color, hover }}
    className={classnames('icon thumbs-down-icon', props.className)}
    viewBox="0 0 14 14"
  >
    <Thumb {...props} transform="rotate(180, 7, 7)" />
  </SVG>
);

export const SubmitIcon = ({
  width = 16,
  height = 16,
  color = 'textPrimary',
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon submit-icon', props.className)}
    viewBox="0 0 16 16"
  >
    <g fill="none" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8" cy="8" r="7.3" />
      <path d="M 5 7.6 L 8 4.6 L 11 7.6 M 8 4.8 v 7.3" />
    </g>
  </SVG>
);

export const SpeechBubbleIcon = ({ width = 16, height = 16, ...props }: SVGProps) => (
  <SVG
    {...{ ...props, width, height }}
    className={classnames('icon text-speech-bubble-icon', props.className)}
    viewBox="0 0 16 16"
  >
    <path
      fill="none"
      strokeLinejoin="round"
      strokeWidth="1.33"
      d="M5.42 2.67h5.16c1.19 0 1.62.12 2.06.35.43.24.77.58 1 1.01.24.44.36.87.36 2.06V7.9c0 1.2-.12 1.62-.36 2.06-.23.43-.57.77-1 1-.44.24-.87.36-2.06.36H8L4.67 14v-2.69a2.9 2.9 0 0 1-1.3-.33 2.42 2.42 0 0 1-1.01-1.01C2.14 9.56 2 9.15 2 8.1V6.09c0-1.2.12-1.62.36-2.06.23-.43.57-.77 1-1 .44-.24.87-.36 2.06-.36Z"
    />
  </SVG>
);

export const HeaderPersonIcon = ({
  width = 18,
  height = 18,
  color = 'primary',
  hover = true,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color, hover }}
    className={classnames('icon header-person-icon', props.className)}
    viewBox="-2 -1.5 18 18"
  >
    <g strokeWidth="1.5" fill="none">
      <path d="M7.2 9.4c3 0 6.1 3.7 6.1 4.1 0 .5-3.0.7-6.1.7-3 0-6.1-.3-6.1-.7 0-.5 3-4.1 6.1-4.1z" />
      <circle cx="7.3" cy="3.7" r="2.8" />
    </g>
  </SVG>
);

export const ClayProfileIcon = ({
  width = 18,
  height = 18,
  color = 'primary',
  hoverOpacity = true,
  ...props
}: SVGProps) => (
  <SVG
    viewBox="-2 -1.5 18 18"
    {...{ ...props, width, height, color, hoverOpacity }}
    className={classnames('icon clay profile-icon', props.className)}
  >
    <g strokeWidth="1.5" fill="none">
      <circle cx="9" cy="5" r="2.6" />
      <path d="M9 10.5c2.63 0 5.25 3.96 5.25 4.45 0 .5-2.63.8-5.25.8-2.63 0-5.25-.3-5.25-.8S6.38 10.5 9 10.5Z" />
    </g>
  </SVG>
);

export const CogIcon = ({
  width = 16,
  height = 16,
  color = 'primary',
  hover = true,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color, hover }}
    className={classnames('icon cog-icon', props.className)}
    viewBox="-1 -1 16 16"
  >
    <g strokeWidth="1.5" fill="none">
      <path d="M11.71,8.91 C11.54,9.3 11.62,9.76 11.92,10.07 L11.96,10.11 C12.2,10.34 12.33,10.67 12.33,11.01 C12.33,11.34 12.2,11.67 11.96,11.91 C11.72,12.15 11.39,12.28 11.06,12.28 C10.72,12.28 10.4,12.15 10.16,11.91 L10.12,11.87 C9.81,11.57 9.35,11.48 8.96,11.66 C8.58,11.82 8.33,12.2 8.32,12.62 L8.32,12.73 C8.32,13.43 7.75,14 7.05,14 C6.35,14 5.78,13.43 5.78,12.73 L5.78,12.67 C5.77,12.24 5.5,11.86 5.09,11.71 C4.7,11.54 4.24,11.62 3.93,11.92 L3.89,11.96 C3.66,12.2 3.33,12.33 2.99,12.33 C2.66,12.33 2.33,12.2 2.09,11.96 C1.85,11.72 1.72,11.39 1.72,11.06 C1.72,10.72 1.85,10.4 2.09,10.16 L2.13,10.12 C2.43,9.81 2.52,9.35 2.34,8.96 C2.18,8.58 1.8,8.33 1.38,8.32 L1.27,8.32 C0.57,8.32 0,7.75 0,7.05 C0,6.35 0.57,5.78 1.27,5.78 L1.33,5.78 C1.76,5.77 2.14,5.5 2.29,5.09 C2.46,4.7 2.38,4.24 2.08,3.93 L2.04,3.89 C1.8,3.66 1.67,3.33 1.67,2.99 C1.67,2.66 1.8,2.33 2.04,2.09 C2.28,1.85 2.61,1.72 2.94,1.72 C3.28,1.72 3.6,1.85 3.84,2.09 L3.88,2.13 C4.19,2.43 4.65,2.52 5.04,2.34 L5.09,2.34 C5.48,2.18 5.73,1.8 5.73,1.38 L5.73,1.27 C5.73,0.57 6.3,0 7,0 C7.7,0 8.27,0.57 8.27,1.27 L8.27,1.33 C8.27,1.75 8.52,2.13 8.91,2.29 C9.3,2.46 9.76,2.38 10.07,2.08 L10.11,2.04 C10.34,1.8 10.67,1.67 11.01,1.67 C11.34,1.67 11.67,1.8 11.91,2.04 C12.15,2.28 12.28,2.61 12.28,2.94 C12.28,3.28 12.15,3.6 11.91,3.84 L11.87,3.88 C11.57,4.19 11.48,4.65 11.66,5.04 L11.66,5.09 C11.82,5.48 12.2,5.73 12.62,5.73 L12.73,5.73 C13.43,5.73 14,6.3 14,7 C14,7.7 13.43,8.27 12.73,8.27 L12.67,8.27 C12.25,8.27 11.87,8.52 11.71,8.91 Z" />
      <circle cx="7" cy="7" r="1.90909091"></circle>
    </g>
  </SVG>
);

export const HeartbeatIcon = ({
  width = 14,
  height = 14,
  color = 'primary',
  strokeWidth = 1.35,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon heartbeat-icon', props.className)}
    viewBox="0 0 14 14"
  >
    <g strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <path
        fill="none"
        strokeWidth={parseFloat(String(strokeWidth)) * 0.82}
        d="M3.6 7h.9l1-1.6l1.5 4.5l2-5.4l0.9 2.5h.6"
      />
      <circle fill="none" cx="7" cy="7" r="6.2" />
    </g>
  </SVG>
);

export const LoaderIcon = ({
  width = 14,
  height = 14,
  color = 'primary',
  r = '0.8',
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon loader-icon animate-spin', props.className)}
    viewBox="0 0 14 14"
  >
    <g stroke="none">
      <circle cx="7" cy="0.9" r={r} />
      <circle cx="11.4" cy="2.6" r={r} />
      <circle cx="13.1" cy="7" r={r} />
      <circle cx="11.4" cy="11.4" r={r} />
      <circle cx="7" cy="13.1" r={r} />
      <circle cx="2.6" cy="11.4" r={r} />
      <circle cx="0.9" cy="7" r={r} />
      <circle cx="2.6" cy="2.6" r={r} />
    </g>
  </SVG>
);

export const Logo = ({ width = 48, height = 29, color = 'textPrimary', ...props }: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('clay-logo', props.className)}
    viewBox="-0.1 -0.3 48 29"
  >
    <path
      stroke="none"
      d="M44.1,0.27 L44.1,14.53 L42.03,14.53 C39.83,14.53 39.7,12.88 39.7,11.79 L39.7,0.27 L36.17,0.27 L36.17,11.08 C36.17,14.62 37.1209614,17.5667052 42.03,17.57 L44.1,17.57 L44.1,22.35 C44.1,23.65 43.67,25.39 42.03,25.39 C40.16,25.39 39.97,23.65 39.97,22.35 L39.97,20.45 L36.48,20.45 C36.48,20.45 36.48,20.81 36.48,21.02 C36.52,24.15 37.02,28.43 42.03,28.43 C46.93,28.43 47.62,24.17 47.62,21.02 L47.62,0.27 L44.1,0.27ZM30.46,25.1 L28.4,25.1 C25.85,25.1 25.73,23.65 25.73,21.99 L25.73,18.21 C25.73,16.71 25.99,15.47 28.4,15.47 L30.47,15.47 L30.47,25.1 Z M28.4,0.03 C23.23,0.03 22.85,4.13 22.85,7.28 L22.85,9.69 L26.34,9.69 L26.34,5.96 C26.34,4.64 26.75,3.07 28.4,3.07 C29.98,3.07 30.47,4.64 30.47,5.96 L30.47,12.43 L28.4,12.43 C24.06,12.43 22.21,14.18 22.21,18.92 L22.21,21.47 C22.21,25.42 23.68,28.14 28.4,28.14 L33.99,28.14 L33.99,7.28 C33.99,4.13 33.15,0.03 28.4026572,0.03 L28.4,0.03ZM17.45,22.35 L17.45,0.29 L13.92,0.29 L13.92,22.35 C13.92,24.45 14.81,28.14 19.55,28.14 L21.21,28.14 L21.21,25.1 L19.59,25.1 C18.43,25.1 17.45,24.79 17.45,22.35ZM8,9.69 L8,5.96 C8,4.64 7.74,3.07 5.81,3.07 C4.05,3.07 3.62,4.64 3.62,5.96 L3.62,22.35 C3.62,24.1 4.15,25.39 5.81,25.34 C7.58,25.39 7.97,24.17 7.97,22.35 L7.97,18.96 L11.5,18.96 L11.5,21.02 C11.5,24.18 11.01,28.43 5.81,28.43 C0.26,28.43 0.09,24.18 0.09,21.02 L0.09,7.27936369 C0.09,4.13 0.69,0.03 5.81,0.03 C11.33,0.03 11.5,4.13 11.5,7.28 L11.5,9.69 L8.01,9.69Z"
    />
  </SVG>
);

export const LogoutIcon = ({ width = 21, height = 16, color = 'primary', ...props }: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon logout-icon', props.className)}
    viewBox="6 6 21 16"
  >
    <g strokeWidth="2" strokeLinecap="round">
      <path
        className="logout-icon-arrow"
        strokeLinejoin="round"
        fill="none"
        d="M23.6 14.23L19.1 18.7l4.5-4.5-4.5-4.5zM21.4 14.2l-9 0"
      />
      <path
        className="logout-icon-bow"
        fill="none"
        d="M9.5,20.5H8.7a2.2 2.2 0 01-2.2-2.2V9.8c0-1.2.98-2.2 2.2-2.2h.7"
      />
    </g>
  </SVG>
);

export const CheckIcon = ({
  width = 17,
  height = 14,
  color = 'primary',
  strokeWidth = 1.9,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon check-icon', props.className)}
    viewBox="0 0 17 14"
  >
    <path
      strokeWidth={strokeWidth}
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M1 8.5L6.2 13L16 1"
    />
  </SVG>
);

export const DoubleCheckIcon = ({
  width = 16,
  height = 19,
  color = 'primary',
  strokeWidth = 1.9,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon double-check-icon', props.className)}
    viewBox="0 0 16 19"
  >
    <g strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <path fill="none" d="M1 7.3L5.5 11.1L13.5 1.4" />
      <path fill="none" d="M2 14.3L6.5 18.1L14.5 8.4" />
    </g>
  </SVG>
);

export const DoubleCheckV2Icon = ({
  width = 24,
  height = 24,
  color = 'primary',
  strokeWidth = 1.9,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon double-check-v2-icon', props.className)}
    viewBox="0 0 24 24"
  >
    <path
      d="M8 9L10.5 11.67L16 5 M8 16L10.5 18.67L16 12"
      fill="none"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </SVG>
);

export const ExpandIcon = ({
  width = 24,
  height = 24,
  color = 'primary',
  strokeWidth = 2,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon expand-icon', props.className)}
    viewBox="0 0 24 24"
  >
    <path
      d="M12 18H7C6.45 18 6 17.55 6 17V12 M18 12V7C18 6.45 17.55 6 17 6H12 M10 14L14 10"
      fill="none"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </SVG>
);

export const ExpandNoteIcon = ({
  width = 24,
  height = 24,
  color = 'primary',
  strokeWidth = 2,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon expand-note-icon', props.className)}
    viewBox="0 0 24 24"
  >
    <path
      d="M6 18h6-6v-6zM18 6v6-6h-6z"
      fill="none"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </SVG>
);

export const ContractNoteIcon = ({
  width = 24,
  height = 24,
  color = 'primary',
  strokeWidth = 2,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon contract-note-icon', props.className)}
    viewBox="0 0 24 24"
  >
    <path
      d="M10 14H4h6v6zM14 10V4v6h6z"
      fill="none"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </SVG>
);

export const GroupIcon = ({
  width = 19,
  height = 10,
  color = 'textSecondary',
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon group-icon', props.className)}
    viewBox="0 0.5 19 10"
  >
    <path
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9.7 6.7c2 0 4 2.4 4 2.7 0 .3-2.0.5-4.0.5s-4-.2-4-.5c0-.3 2-2.7 4-2.7zm0.1-1.2a1.5 1.5 0 100-4.4 1.5 1.5 0 000 4.4zM13.8 2.2a1.6 1.6 0 010 3.1M15.3 7c1.3.5 2.3 1.8 2.3 2 0 .2-.6.2-2.3.4M5.2 2.2a1.6 1.6 0 000 3.1M3.8 7c-1.3.5-2.3 1.8-2.3 2 0 .2.6.3 2.3.4"
    />
  </SVG>
);

export const MagnifyingGlassIcon = ({
  width = 16,
  height = 16,
  color = 'primary',
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon magnifying-glass-icon', props.className)}
    viewBox="0 0 16 16"
  >
    <path
      strokeWidth="1.6"
      fill="none"
      strokeLinecap="round"
      d="M1 6 a4.8 4.8 0 1 0 9.6 0a4.8 4.8 0 1 0 -9.6 0 M11.2 11.2l3.1 3.1"
    />
  </SVG>
);

export const ReviewViewIcon = ({
  width = 24,
  height = 24,
  color = 'textPrimary',
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon review-view-icon', props.className)}
    viewBox="0 0 24 24"
  >
    <path
      strokeWidth="2"
      strokeLinecap="round"
      d="M8 21H17M3 17.5H21M12 2V3M2 12H3M21 12H22M4.9 4.9L5.6 5.6M18.4 5.6L19.1 4.9M16.6 14C16.9 13.4 17 12.8 17 12.1C17 9.3 14.8 7 12 7C9.2 7 7 9.3 7 12.1C7 12.8 7.1 13.4 7.4 14"
    />
  </SVG>
);

export const NavPeopleIcon = ({
  width = 17,
  height = 17,
  color = 'textSecondary',
  strokeWidth = 2,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon nav-people-icon', props.className)}
    viewBox="0 0 24 24"
  >
    <defs>
      <circle id="navPeopleCirc" cx="12" cy="12" r="9.5" />
      <mask id="navPeopleMask" fill="#fff">
        <use xlinkHref="#navPeopleCirc" />
      </mask>
    </defs>
    <g strokeWidth={strokeWidth}>
      <use fill="none" xlinkHref="#navPeopleCirc" />
      <path
        d="M12 16.5c3.25 0 6.5 4.04 6.5 4.54s-3.25 2.31-6.5 2.31-6.5-1.8-6.5-2.31c0-.5 3.25-4.54 6.5-4.54Z"
        fill="none"
        mask="url(#navPeopleMask)"
      />
      <circle fill="none" cx="12" cy="9.75" r="2.75" />
    </g>
  </SVG>
);

export const PersonProfileViewIcon = ({
  width = 24,
  height = 24,
  color = 'textPrimary',
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon person-profile-view-icon', props.className)}
    viewBox="0 0 24 24"
  >
    <path
      fill="none"
      id="personProfileViewPetals"
      d="M20 4C20.3 4.4 20.5 4.8 20.5 5.3C20.5 5.8 20.5 6.3 20.2 6.9C19.9 7.8 19.2 8.9 18.1 9.9C16.8 11.3 15 12 12.7 12C12.5 12 12.3 12 12 12C12 11.8 12 11.5 12 11.3C12 9.1 12.7 7.3 14.1 5.9C15.1 4.9 16.2 4.1 17.2 3.8C17.7 3.6 18.2 3.5 18.7 3.5C19.2 3.5 19.7 3.7 20 4ZM9.8 5.1C10 3.3 10.9 2 12 2C13.1 2 14 3.3 14.3 5.1"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <use href="#personProfileViewPetals" transform="rotate(90 12 12)" />
    <use href="#personProfileViewPetals" transform="rotate(180 12 12)" />
    <use href="#personProfileViewPetals" transform="rotate(270 12 12)" />
  </SVG>
);

export const NavNotesIcon = ({
  width = 16,
  height = 16,
  color = 'textSecondary',
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon nav-notes-icon', props.className)}
    viewBox="0 0 24 24"
  >
    <g strokeLinecap="round" strokeWidth="2">
      <rect fill="none" strokeLinejoin="round" x="3" y="3" width="18" height="18" rx="4" />
      <path fill="none" d="M8 17h4M8 12.5h8M3 8.5h18" />
    </g>
  </SVG>
);

export const NavRemindersIcon = ({
  width = 12,
  height = 16,
  color = 'textSecondary',
  strokeWidth = 0.65,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon nav-reminders-icon', props.className)}
    viewBox="0 0 12 16"
  >
    <path
      d="M1.51973804,8.41742113 C1.05052201,8.41710371 0.918673083,8.12132713 1.21679457,7.76683422 L6.66708339,1.28595693 C6.96898836,0.926965118 7.15294846,1.02289456 7.08211924,1.47380504 L6.27201112,6.63108743 C6.19932412,7.09382489 6.52714159,7.46894771 6.98845587,7.46894771 L9.48307077,7.46894771 C9.95143928,7.46894771 10.0827856,7.75918482 9.77852353,8.11477648 L4.13101002,14.7150369 C3.82581556,15.0717182 3.64776532,14.9876777 3.73659217,14.5097408 L4.71027219,9.2708113 C4.79763569,8.80074805 4.48101605,8.4194244 4.01886701,8.41911176 L1.51973804,8.41742113 Z"
      strokeWidth={strokeWidth}
    />
  </SVG>
);

export const NavHomeIcon = ({
  width = 15,
  height = 17,
  strokeWidth = 1.7,
  color = 'textSecondary',
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon nav-home-icon', props.className)}
    viewBox="0 0 18 20"
  >
    <g strokeWidth={strokeWidth}>
      <path
        fill="none"
        d="M1.4 7L9 1L16.6 7C16.8 7.2 17 7.5 17 7.8V17.2C17 18.2 16.2 19 15.2 19H2.8C1.8 19 1 18.2 1 17.2V7.8C1 7.5 1.2 7.2 1.4 7Z"
        strokeLinejoin="round"
      />
      <rect x="5" y="13" width="2" height="2" rx="0.1" />
    </g>
  </SVG>
);

export const NotesIcon = ({
  width = 14,
  height = 14,
  color = 'primary',
  strokeWidth = 1.3,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon notes-icon', props.className)}
    viewBox="-1 -1 14 14"
  >
    <rect strokeWidth={strokeWidth} x="0.65" y="0.65" width="7.1" height="7.1" rx="1.3" />
    <rect strokeWidth={strokeWidth} fill="none" x="3.2" y="3.2" width="7.8" height="7.7" rx="1.3" />
  </SVG>
);

export const OfflineIcon = ({ width = 24, height = 24, color = 'primary', ...props }: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon offline-icon', props.className)}
    viewBox="0 0 24 24"
  >
    <g strokeWidth="2" strokeLinecap="round" fill="none">
      <path d="M18.6 9.4C20.6 9.7 22.1 11.4 22.1 13.4C22.1 15.7 20.2 17.5 18 17.5C14.1 17.5 13.3 17.5 10.6 17.5" />
      <path d="M3 15.9C2.4 15.1 2 14.2 2 13.1C2 10.7 3.9 8.7 6.3 8.6L6.4 8.6C6.5 8.6 6.5 8.6 6.5 8.6C7.4 6.8 9.3 5.5 11.4 5.5C12 5.5 12.6 5.6 13.2 5.8C13.3 5.8 13.4 5.9 13.6 5.9" />
      <path d="M20 4L4 20" />
    </g>
  </SVG>
);

export const OpenLinkIcon = ({
  width = 24,
  height = 24,
  color = 'primary',
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon open-link-icon', props.className)}
    viewBox="0 0 16 16"
  >
    <path
      d="M6 2.7h-.1c-1.1 0-1.7 0-2.1.2a2 2 0 0 0-.8.9c-.2.4-.2 1-.2 2.1v4.3c0 1.1 0 1.7.2 2.1a2 2 0 0 0 .9.9c.4.2 1 0.2 2.1.2h4.3c1.1 0 1.7 0 2.1-.2a2 2 0 0 0 .9-.9c.2-.4.2-1 0.2-2.1V10m-4-7.3h4m0 0v4m0-4-6 6"
      fill="none"
      strokeWidth="1.33"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </SVG>
);

export const PenEditedIcon = ({
  width = 16,
  height = 16,
  color = 'primary',
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon pen-edited-icon', props.className)}
    viewBox="0 0 24 24"
  >
    <path
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M15.71 3.6a3 3 0 0 1 2.9 5.03L9.36 17.9 3.5 19.5l1.6-5.85 9.27-9.27a3 3 0 0 1 1.34-.78ZM12.2 6.55 l4.25 4.24M20 18.5h-6"
    />
  </SVG>
);

export const PlusIcon = ({
  width = 15,
  height = 15,
  color = 'primary',
  strokeWidth = 2,
  ...props
}: SVGProps) => (
  <SVG
    viewBox="0 0 15 15"
    {...{ ...props, width, height, color }}
    className={classnames('icon plus-icon', props.className)}
  >
    <path strokeWidth={strokeWidth} fill="none" strokeLinecap="round" d="M1 7.5H14M7.5 1V14" />
  </SVG>
);

export const RepeatIcon = ({
  width = 12,
  height = 10,
  color = 'textSecondary',
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon repeat-icon', props.className)}
    viewBox="0 0 12 10"
  >
    <path
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M10.9 6.1v2.3-2.3H8.7zM8.9 8a4.3 4.3 0 01-6.8-2M1.3 3.5V1.1v2.3h2.2zM3.4 1.5a4.3 4.3 0 016.8 2.1"
    />
  </SVG>
);

export const RecentIcon = ({
  width = 13,
  height = 13,
  color = 'textPrimary',
  ...props
}: SVGProps) => (
  <SVG {...{ ...props, width, height, color }} className={classnames('icon', props.className)}>
    <path
      fill="none"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M1.1 5.02V2.14v2.88h2.737z"
    />
    <path
      d="M10.812 11.172a5.925 5.925 0 00-7.617-9.078.675.675 0 00.868 1.034A4.575 4.575 0 112.639 8.01a.675.675 0 00-1.288.406 5.925 5.925 0 009.46 2.757z"
      stroke="none"
    />
  </SVG>
);

const ReconnectIcon = ({
  width = 18,
  height = 18,
  color = 'textPrimary',
  hoverOpacity = true,
  children,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color, hoverOpacity }}
    className={classnames('icon reconnect-icon', props.className)}
    viewBox="0.5 0.5 19 19"
  >
    <g transform="translate(2, 2)">
      <rect
        strokeWidth="1.4"
        x="-0.7"
        y="-0.7"
        width="17.4"
        height="17.4"
        rx="4.4"
        fill="none"
      ></rect>
      <path
        stroke="none"
        d="M2,0 L14,0 C15.1,0 16,0.9 16,2 L16,4.7 L16,4.7 L0,4.7 L0,2 C0,0.8954305 0.9,0 2,0 Z"
        id="header"
        opacity="0.2"
      ></path>
      <path
        fill="none"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4.55 4.32h6.85m-1.8 -1.9l1.8 1.9 l-1.8 1.9"
      />
      {children}
    </g>
  </SVG>
);

export const ReconnectWkIcon = (props: SVGProps) => (
  <ReconnectIcon {...props}>
    <g transform="translate(2.65, 7.95)">
      <path
        stroke="none"
        d="M1.77,5.31 C2.11,5.31 2.3,5.15 2.41,4.77 L3.26,1.72 L3.29,1.72 L4.15,4.77 C4.26,5.15 4.45,5.31 4.79,5.31 C5.13,5.31 5.31,5.15 5.42,4.77 L6.5,0.85 C6.54,0.72 6.56,0.59 6.56,0.49 C6.56,0.19 6.36,0.01 6.03,0.01 C5.74,0.01 5.57,0.16 5.5,0.47 L4.73,3.82 L4.7,3.82 L3.83,0.48 C3.75,0.16 3.57,0.01 3.28,0.01 C2.98,0.01 2.81,0.15 2.72,0.49 L1.85,3.82 L1.83,3.82 L1.05,0.47 C0.99,0.16 0.82,0.01 0.53,0.01 C0.2,0.01 0,0.19 0,0.49 C0,0.59 0.02,0.72 0.06,0.85 L1.14,4.76 C1.24,5.15 1.43,5.31 1.77,5.31 Z"
      />
      <path
        stroke="none"
        d="M7.71,5.32 C8.01,5.32 8.23,5.13 8.23,4.78 L8.23,4 L8.56,3.69 L9.55,5 C9.71,5.24 9.84,5.32 10.06,5.32 C10.35,5.32 10.56,5.13 10.56,4.85 C10.56,4.71 10.52,4.62 10.41,4.47 L9.34,3.1 L10.32,2.18 C10.47,2.04 10.51,1.94 10.51,1.78 C10.51,1.53 10.32,1.33 10.05,1.33 C9.89,1.33 9.77,1.39 9.63,1.54 L8.24,2.93 L8.23,2.93 L8.23,0.55 C8.23,0.19 8.01,0 7.71,0 C7.4,0 7.18,0.19 7.18,0.55 L7.18,4.78 C7.18,5.13 7.4,5.32 7.71,5.32 Z"
      />
    </g>
  </ReconnectIcon>
);

export const ReconnectMoIcon = (props: SVGProps) => (
  <ReconnectIcon {...props}>
    <g transform="translate(3.3, 7.95)">
      <path
        stroke="none"
        d="M0.48,5.31 C0.79,5.31 0.96,5.12 0.96,4.8 L0.96,1.75 L0.99,1.75 L2.27,4.88 C2.36,5.1 2.51,5.21 2.74,5.21 C2.96,5.21 3.11,5.11 3.2,4.88 L4.48,1.75 L4.51,1.75 L4.51,4.8 C4.51,5.12 4.68,5.31 4.99,5.31 C5.29,5.31 5.47,5.12 5.47,4.8 L5.47,0.7 C5.47,0.26 5.21,0 4.78,0 C4.4,0 4.19,0.16 4.03,0.56 L2.75,3.73 L2.72,3.73 L1.44,0.56 C1.28,0.16 1.07,0 0.7,0 C0.26,0 0,0.26 0,0.7 L0,4.8 C0,5.12 0.18,5.31 0.48,5.31 Z"
      ></path>
      <path
        stroke="none"
        d="M8,5.32 C9.16,5.32 9.9,4.58 9.9,3.32 C9.9,2.07 9.15,1.32 8,1.32 C6.85,1.32 6.1,2.07 6.1,3.32 C6.1,4.58 6.84,5.32 8,5.32 Z M8,4.52 C7.49,4.52 7.16,4.09 7.16,3.32 C7.16,2.56 7.49,2.12 8,2.12 C8.51,2.12 8.84,2.56 8.84,3.32 C8.84,4.09 8.51,4.52 8,4.52 Z"
      ></path>
    </g>
  </ReconnectIcon>
);

export const ReconnectQrIcon = (props: SVGProps) => (
  <ReconnectIcon {...props}>
    <g transform="translate(4.04, 7.95)">
      <path
        stroke="none"
        d="M2.49,5.34 C2.82,5.34 3.12,5.3 3.39,5.2 L3.71,5.66 C3.82,5.82 3.93,5.89 4.11,5.89 C4.36,5.89 4.55,5.72 4.55,5.48 C4.55,5.37 4.52,5.29 4.45,5.18 L4.16,4.76 C4.69,4.31 4.98,3.58 4.98,2.67 C4.98,1.03 4.02,0 2.49,0 C0.96,0 0,1.03 0,2.67 C0,4.33 0.96,5.34 2.49,5.34 Z M2.49,4.45 C1.64,4.45 1.1,3.76 1.1,2.67 C1.1,1.59 1.64,0.89 2.49,0.89 C3.34,0.89 3.88,1.59 3.88,2.67 C3.88,3.17 3.74,3.58 3.5,3.86 L3.26,3.53 C3.17,3.41 3.07,3.36 2.92,3.36 C2.69,3.36 2.51,3.52 2.51,3.75 C2.51,3.84 2.54,3.94 2.61,4.04 L2.86,4.4 C2.75,4.43 2.62,4.45 2.49,4.45 Z"
      ></path>
      <path
        stroke="none"
        d="M6.11,5.33 C6.43,5.33 6.64,5.13 6.64,4.79 L6.64,3.08 C6.64,2.63 6.8,2.36 7.28,2.35 C7.48,2.35 7.63,2.3 7.73,2.22 C7.83,2.13 7.88,2.01 7.88,1.84 C7.88,1.66 7.83,1.53 7.73,1.45 C7.66,1.39 7.55,1.36 7.41,1.36 C7.03,1.36 6.71,1.64 6.61,2.12 L6.59,2.12 L6.59,1.87 C6.59,1.53 6.39,1.34 6.09,1.34 C5.79,1.34 5.59,1.53 5.59,1.87 L5.59,4.79 C5.59,5.13 5.8,5.33 6.11,5.33 Z"
      ></path>
    </g>
  </ReconnectIcon>
);

export const ReconnectYrIcon = (props: SVGProps) => (
  <ReconnectIcon {...props}>
    <g id="Yr" transform="translate(4.5, 7.95)">
      <path
        stroke="none"
        d="M2.23,5.31 C2.57,5.31 2.77,5.1 2.77,4.74 L2.77,3.31 L4.34,0.87 C4.42,0.74 4.46,0.63 4.46,0.5 C4.46,0.21 4.23,0 3.92,0 C3.71,0 3.57,0.1 3.43,0.33 L2.25,2.28 L2.22,2.28 L1.04,0.33 C0.9,0.09 0.77,0 0.54,0 C0.23,0 0,0.21 0,0.51 C0,0.63 0.03,0.75 0.11,0.87 L1.69,3.31 L1.69,4.74 C1.69,5.1 1.89,5.31 2.23,5.31 Z"
      ></path>
      <path
        stroke="none"
        d="M5.15,5.31 C5.47,5.31 5.68,5.12 5.68,4.77 L5.68,3.07 C5.68,2.61 5.84,2.34 6.32,2.33 C6.52,2.33 6.67,2.28 6.77,2.2 C6.875,2.11 6.93,1.99 6.93,1.82 C6.93,1.64 6.875,1.51 6.77,1.44 C6.7,1.37 6.59,1.34 6.45,1.34 C6.07,1.34 5.75,1.62 5.65,2.1 L5.63,2.1 L5.63,1.85 C5.63,1.51 5.43,1.32 5.13,1.32 C4.83,1.32 4.63,1.51 4.63,1.85 L4.63,4.77 C4.63,5.12 4.84,5.31 5.15,5.31 Z"
      ></path>
    </g>
  </ReconnectIcon>
);

export const ShareMenuIcon = ({
  width = 18,
  height = 24,
  color = 'primary',
  hoverOpacity = true,
  isOpen,
  ...props
}: SVGProps & { isOpen?: boolean }) => (
  <SVG
    {...{ ...props, width, height, color, hoverOpacity: isOpen ? false : hoverOpacity }}
    className={classnames(
      'icon share-menu-icon',
      props.className,
      styles.shareIcon,
      isOpen && styles.shareIconOpen,
    )}
    viewBox="4 0.5 18 24"
  >
    <g
      fill="none"
      strokeLinecap="round"
      strokeWidth="1.373"
      strokeLinejoin="round"
      transform="scale(1.25) translate(1.5, 2.17)"
    >
      <path d="M7 3.4C6.6,3.4 5.9,3.4 5.1,3.4C3.9,3.4 3,4.3 3,5.5L3,11C3,12.1 3.9,13 5.1,13L10.5,13C11.7,13 12.6,12.1 12.6,11C12.6,10.4 12.6,9.6 12.6,9" />
      <path className={styles.shareArrow} d="M10.5 2.6H13.5V5.6M9.1 6.9L13.4 2.6" />
    </g>
  </SVG>
);

export const CopyLinkIcon = ({
  width = 16,
  height = 16,
  color = 'primary',
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon copy-link-icon', props.className)}
    viewBox="0 0 16 16"
  >
    <path
      fill="none"
      id="shareIconLink"
      d="M7.1 8.9L6.6 8.5C5.5 7.4 5.5 5.7 6.6 4.7L8 3.3C9 2.2 10.7 2.2 11.8 3.3L12.7 4.2C13.8 5.3 13.8 7 12.7 8"
      strokeWidth="1.3"
      strokeLinecap="round"
    />
    <use href="#shareIconLink" transform="rotate(180 8 8)" />
  </SVG>
);

export const ShareActionIcon = ({
  width = 16,
  height = 16,
  color = 'primary',
  isShared = false,
  ...props
}: SVGProps & { isShared?: boolean }) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon share-action-icon', props.className)}
    viewBox="0 0 24 24"
  >
    <g strokeWidth="2" strokeLinecap="round">
      <path
        fill="none"
        d="M7.8 12C6.6 12.6 5.7 13.4 4.8 14.3C4 15.3 3.4 16.4 3 17.6M7.4 8.3C6.1 7.9 5.4 6.5 5.8 5.2C6.2 3.9 7.6 3.2 8.9 3.6"
      />
      <g fill={isShared ? 'inherit' : 'none'}>
        <path d="M14.5 14.5C11.3 14.5 8 19 8 19.6C8 20.2 11.3 20.5 14.5 20.5C17.8 20.5 21 20.2 21 19.6C21 19 17.8 14.5 14.5 14.5Z" />
        <circle cx="14.5" cy="8" r="3" />
      </g>
    </g>
  </SVG>
);

export const ContactActionIcon = ({
  width = 16,
  height = 16,
  color = 'primary',
  hoverOpacity = true,
  isOpen,
  ...props
}: SVGProps & { isOpen?: boolean }) => (
  <SVG
    viewBox="0 0 24 24"
    {...{ ...props, width, height, color, hoverOpacity: isOpen ? false : hoverOpacity }}
    className={classnames(
      'icon contact-action-icon',
      props.className,
      styles.contactActionIcon,
      isOpen && styles.contactActionIconOpen,
    )}
  >
    <path
      fill="none"
      d="M9.5 4h5c5.53 0 7.5 3.03 7.5 7.5 0 4.47-2.02 7.5-7.5 7.5h-5c-.92 0-3.09.33-6.5 1 .49-2.2.53-3.72.13-4.55A9.03 9.03 0 0 1 2 11.5C2 7.14 4.01 4 9.5 4Z"
      strokeWidth="1.9"
      strokeLinejoin="round"
    />
    <circle strokeWidth="0.5" cx="7.5" cy="11.5" r="1" />
    <circle strokeWidth="0.5" cx="12" cy="11.5" r="1" />
    <circle strokeWidth="0.5" cx="16.5" cy="11.5" r="1" />
  </SVG>
);

export const ShortcutsIcon = ({
  width = 14,
  height = 16,
  color = 'primary',
  hover = true,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color, hover }}
    className={classnames('icon shortcuts-icon', props.className)}
    viewBox="-1 -1 14 16"
  >
    <path
      d="M12,4.4 L12,11.375 C12,12.8 10.8,14 9.4,14 L2.6,14 C1.2,14 0,12.8 0,11.4 L0,4.4M3,0.7 C4,0.9 5,1 6,1 C7,1 8,0.9 9,0.7 C10.4,0.5 11.7,1.5 12,2.9 C12,3 12,3.2 12,3.3 L12,8 C12,9.3 11.1,10.3 9.9,10.6 C8.6,10.9 7.3,11 6,11 C4.7,11 3.4,10.9 2.1,10.6 C0.9,10.3 0,9.3 0,8 L0,3.3 C0,1.9 1.2,0.7 2.625,0.7 C2.8,0.7 2.9,0.7 3,0.7 Z"
      fill="none"
      strokeWidth="1.4"
    ></path>
    <path
      d="M3.5,7.1 C3.7,8.2 4.6,8.8 6.1,8.8 C7.8,8.8 8.5,8.1 8.5,7.2 C8.5,6.5 8.1,5.8 6.2,5.6 C5.1,5.4 4.9,5.2 4.9,4.9 C4.9,4.6 5,4.3 5.9,4.3 C6.6,4.3 6.9,4.5 7.1,4.7 C7.1,4.8 7.2,4.9 7.2,5 L8.3,5 C8.3,4.9 8.3,4.8 8.2,4.7 C8.1,4.1 7.5,3.5 6,3.5 C4.3,3.5 3.8,4.3 3.8,5 C3.8,5.6 4.1,6.3 5.9,6.5 C7.1,6.7 7.4,6.9 7.4,7.3 C7.4,7.7 7.1,8 6.2,8 C5.3,8 4.8,7.7 4.7,7.1 L3.5,7.1 Z"
      stroke="none"
    ></path>
  </SVG>
);

export const TrashIcon = ({
  width = 20,
  height = 20,
  color = 'primary',
  hoverOpacity = true,
  viewBox = '0 0 16 16',
  strokeWidth = 1.65,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color, hoverOpacity }}
    className={classnames('icon trash-icon', props.className)}
    viewBox={viewBox}
  >
    <path
      fill="none"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M6.3 8.7H9.7M14 5.3H2L2.8 12.5C2.9 13.4 3.7 14 4.7 14H11.3C12.3 14 13.1 13.4 13.2 12.5L14 5.3Z"
    />
    <rect fill="none" x="1.3" y="2" width="13.3" height="3.3" rx="1.2" strokeWidth={strokeWidth} />
  </SVG>
);

export const ViewReconnectIcon = ({
  width = 14,
  height = 16,
  color = 'primary',
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon view-reconnect-icon', props.className)}
    viewBox="0 0 14 16"
  >
    <rect x=".6" y=".6" width="12.8" height="14.8" fill="none" rx="2.5" strokeWidth="1.2" />
    <circle cx="4.65" cy="4.65" r="1.65" stroke="none" />
  </SVG>
);

export const SyncIcon = ({
  width = 16,
  height = 16,
  color = 'textSecondary',
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon sync-icon', props.className)}
    viewBox="0 0 16 16"
  >
    <path
      fill="none"
      strokeLinecap="round"
      strokeWidth="1.33"
      strokeLinejoin="round"
      d="M2 9.32a5.98 5.98 0 0 0 1.9 3.16 6.19 6.19 0 0 0 7.08.75M14 6.91a5.96 5.96 0 0 0-1.77-3.23 6.18 6.18 0 0 0-7.05-1.02M2 3.33V6h2.67M14 12.67V10h-2.67"
    />
  </SVG>
);

export const ResetIcon = ({
  width = 24,
  height = 24,
  color = 'textSecondary',
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon sync-icon', props.className)}
    viewBox="0 0 24 24"
  >
    <g strokeLinecap="round" fill="none" strokeWidth={2}>
      <path strokeLinejoin="round" d="M21 5v4h-4" />
      <path d="M20.046 16.038A9 9 0 1 1 16.976 4.5" />
    </g>
    <circle cx={12} cy={12} r={2} />
  </SVG>
);

export const RedoIcon = ({
  width = 16,
  height = 16,
  color = 'textSecondary',
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon redo-icon', props.className)}
    viewBox="0 0 16 16"
  >
    <path
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.33"
      d="M2.67 4v2.67h2.66M3.38 10.67a5.33 5.33 0 1 0 2.03-7.33"
    />
  </SVG>
);

export const XIcon = ({
  width = 15,
  height = 15,
  color = 'primary',
  strokeWidth = 2,
  ...props
}: SVGProps) => (
  <SVG
    className={classnames('icon x-icon', props.className)}
    viewBox="0 0 15 15"
    {...{ ...props, width, height, color }}
  >
    <path
      strokeWidth={strokeWidth}
      fill="none"
      strokeLinecap="round"
      d="M1.8 1.8L13.2 13.2M13.2 1.8L1.8 13.2"
    />
  </SVG>
);

export const ArrowRightIcon = ({
  width = 8,
  height = 8,
  color = 'textSecondary',
  strokeWidth = 1.1,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon arrow-right-icon', props.className)}
    viewBox="0 0 8 8"
  >
    <path
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={strokeWidth}
      d="M 0.8 4 h 6 M 4.1 1 L 7 4 L 4.1 7"
    />
  </SVG>
);

export const RegenerateIcon = ({
  width = 16,
  height = 15,
  color = 'primary',
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon regenerate-icon', props.className)}
    viewBox="2.3 4.1 16 15"
  >
    <defs>
      <g id="regenerateIconArrow">
        <path strokeWidth="1.5" d="M 6.5 7 A 5.9 5.9 1 0 1 15.9 10" />
        <path strokeWidth="1.55" d="M 3.65 6.6 v 3.2 h 3.2" />
      </g>
    </defs>
    <g fill="none" strokeLinecap="round" strokeLinejoin="round">
      <use href="#regenerateIconArrow" />
      <use href="#regenerateIconArrow" transform="rotate(180, 10.3, 11.6)" />
    </g>
  </SVG>
);

export const RestoreIcon = ({ width = 16, height = 16, color = 'primary', ...props }: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color }}
    className={classnames('icon restore-icon', props.className)}
    viewBox="0 0 16 16"
  >
    <path
      fill="none"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.4 10.1C2.8 11.2 3.6 12.2 4.6 12.9C5.6 13.7 6.9 14 8.1 14C9.4 14 10.6 13.6 11.6 12.8C12.6 12.1 13.3 11 13.7 9.8C14.1 8.6 14.1 7.4 13.7 6.2C13.3 5 12.6 3.9 11.6 3.2C10.6 2.4 9.4 2 8.1 2C6.9 2 5.6 2.3 4.6 3.1M2 3L2 6L4.7 6"
    />
  </SVG>
);

export const SunriseIcon = ({ width = 15, height = 15, ...props }: SVGProps) => (
  <SVG
    {...{ ...props, width, height }}
    className={classnames('icon sunrise-icon', props.className)}
    viewBox="0 0 15 15"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
      fill="none"
      d="M4.13 14.25h6.75M1.5 11.25h12 M10.77 8.25a3.34 3.34 0 1 0-6.62 0M.75 7.88h.75M13.5 7.88h.75M7.5 1.13h0M2.63 2.63l.74.74M12.37 2.63l-.74.74M7.5.75v.75"
    />
  </SVG>
);

export const AlarmClockIcon = ({ width = 15, height = 15, ...props }: SVGProps) => (
  <SVG
    {...{ ...props, width, height }}
    className={classnames('icon alarm-clock-icon', props.className)}
    viewBox="0 -0.375 15 15"
  >
    <g fill="none" strokeWidth="1.5">
      <circle cx="7.5" cy="8.25" r="5.25" />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M7.31 5.81v3l1.32 1.5M4.05.75A8.28 8.28 0 0 0 .75 3.5m13.5.02c-.84-1.2-2-2.16-3.34-2.77"
      />
    </g>
  </SVG>
);

export const CreditCardIcon = ({ width = 15, height = 15, ...props }: SVGProps) => (
  <SVG
    {...{ ...props, width, height }}
    className={classnames('icon credit-card-icon', props.className)}
    viewBox="0 -0.75 15 15"
  >
    <g fill="none" strokeWidth="1.5">
      <rect width="13.5" height="12" x="0.75" y="0.75" rx="2.25" />
      <path strokeLinecap="round" d="M0.75 4.13h13.5M3.75 7.5h5.63" />
    </g>
  </SVG>
);

export const StarredIcon = ({
  width = 14,
  height = 14,
  isLight,
  ...props
}: SVGProps & { isLight: boolean }) => (
  <SVG {...{ ...props, width, height }} viewBox="0 0 14 14">
    <defs>
      <path
        id="starGraphic"
        stroke="none"
        d="M7 9.25l2.7 1.4l-0.5 -2.9l2.1 -2.1l-3 -0.5l-1.3 -2.6l-1.3 2.6l-3 0.5l2.1 2.1l-0.5 2.9z"
      />
      <linearGradient id="starGraphicLG1" x1="50%" x2="54.83%" y1="0%" y2="100%">
        <stop offset="0%" stopColor="#FFC933" />
        <stop offset="100%" stopColor="#FFB129" />
      </linearGradient>
      <linearGradient id="starGraphicLG2" x1="50%" x2="54.83%" y1="0%" y2="100%">
        <stop offset="0%" stopColor="#F2BE12" />
        <stop offset="100%" stopColor="#FFB129" />
      </linearGradient>
    </defs>
    <g stroke="none">
      <use href="#starGraphic" fill={isLight ? 'url(#starGraphicLG1)' : undefined} />
      <use href="#starGraphic" fill={isLight ? 'url(#starGraphicLG2)' : undefined} />
    </g>
  </SVG>
);

export const BadgeStarredIcon = ({ width = 14, height = 14, ...props }: SVGProps) => {
  const [_, theme] = useTheme();
  const isLight = theme === 'light';

  return (
    <div
      className={classnames(
        styles.badgeV2,
        props.className,
        'badge-starred-icon',
        isLight && styles.badgeStarredIconLight,
      )}
      style={{ width: `${width}px`, height: `${height}px` }}
    >
      <StarredIcon {...{ ...props, width, height, isLight }} />
    </div>
  );
};

export const BadgeSharedWithMeIcon = ({
  width = 14,
  height = 14,
  className,
  ...props
}: SVGProps) => {
  return (
    <div
      className={classnames(styles.badgeV2, className, 'badge-shared-with-me-icon')}
      style={{ width: `${width}px`, height: `${height}px` }}
    >
      <SVG {...{ ...props, width, height }} viewBox="-2.5 -2 14 14">
        <g className={styles.badge}>
          <path
            d="M6.3 6C5 6 3.7 8 3.7 8.2C3.7 8.5 5 8.6 6.3 8.6C7.6 8.6 8.9 8.5 8.9 8.2C8.9 8 7.6 6 6.3 6Z"
            strokeWidth="0.8"
          />
          <circle stroke="none" cx="6.25" cy="3.18" r="1.68" />
          <path
            d="M3.2 4.77C3.1 4.58 2.87 4.51 2.69 4.61C2.1 4.93 1.61 5.33 1.2 5.81C0.8 6.29 0.49 6.85 0.27 7.48C0.2 7.67 0.31 7.89 0.5 7.96C0.69 8.04 0.9 7.93 0.97 7.73C1.16 7.19 1.42 6.72 1.76 6.32C2.11 5.91 2.54 5.57 3.05 5.28C3.23 5.19 3.29 4.95 3.2 4.77ZM3.51 0.57C2.71 0.31 1.85 0.77 1.58 1.59C1.31 2.41 1.74 3.29 2.54 3.55C2.74 3.61 2.95 3.5 3.01 3.3C3.08 3.1 2.97 2.89 2.78 2.82C2.37 2.69 2.15 2.24 2.29 1.82C2.42 1.4 2.87 1.17 3.27 1.3C3.47 1.36 3.68 1.25 3.75 1.05C3.81 0.85 3.71 0.63 3.51 0.57Z"
            stroke="none"
          />
        </g>
      </SVG>
    </div>
  );
};

export const BadgeAddressIcon = ({ width = 14, height = 14, ...props }: SVGProps) => (
  <SVG {...{ ...props, width, height }}>
    <defs>
      <linearGradient
        id="badgeAddressGradientA"
        x1="99.254%"
        x2="19.434%"
        y1="98.944%"
        y2="-14.028%"
      >
        <stop offset="0%" stopColor="#FF7E44" />
        <stop offset="100%" stopColor="#FFD34F" />
      </linearGradient>
      <linearGradient
        id="badgeAddressGradientB"
        x1="-.609%"
        x2="70.923%"
        y1="-75.802%"
        y2="134.422%"
      >
        <stop offset="0%" stopColor="#FFF099" />
        <stop offset="32.386%" stopColor="#FFD34F" />
        <stop offset="69.69%" stopColor="#FFB74A" />
        <stop offset="100%" stopColor="#FF7E44" />
      </linearGradient>
    </defs>
    <rect
      width="14"
      height="14"
      fill="url(#badgeAddressGradientA)"
      fillOpacity="0.32"
      rx="4"
      stroke="none"
    />
    <path
      fill="url(#badgeAddressGradientB)"
      stroke="none"
      d="M7 2c2.21 0 4 1.832 4 4.09C11 9.274 7 12 7 12S3 9.273 3 6.09C3 3.833 4.79 2 7 2zm0 2.727c-.736 0-1.333.61-1.333 1.364 0 .753.597 1.364 1.333 1.364s1.333-.611 1.333-1.364c0-.753-.597-1.364-1.333-1.364z"
      opacity="0.96"
    />
  </SVG>
);

export const CalendarIcon = ({ width = 14, height = 14, ...props }: SVGProps) => (
  <SVG {...{ ...props, width, height }}>
    <defs>
      <linearGradient id="calIconLG">
        <stop offset="0" stopColor="#8fc660" />
        <stop offset="1" stopColor="#569b94" />
      </linearGradient>
      <linearGradient
        id="calIconLG_a"
        gradientUnits="userSpaceOnUse"
        x1="3.6"
        x2="9.8"
        xlinkHref="#calIconLG"
        y1="3.8"
        y2="5"
      />
      <linearGradient
        id="calIconLG_b"
        gradientUnits="userSpaceOnUse"
        x1="3.1"
        x2="10.7"
        xlinkHref="#calIconLG"
        y1="3.2"
        y2="10.6"
      />
      <linearGradient
        id="calIconLG_c"
        gradientUnits="objectBoundingBox"
        x1="0"
        x2="0.9"
        xlinkHref="#calIconLG"
        y1="0"
        y2="0.9"
      />
      <rect
        id="calIconDay"
        width="1.3"
        height="1.3"
        fill="url(#calIconLG_c)"
        stroke="none"
        rx="0.3"
      />
    </defs>
    <rect
      x="2.8"
      y="2.8"
      width="8.4"
      strokeWidth="0"
      height="2"
      rx="1.2"
      fill="url(#calIconLG_a)"
    />
    <rect
      x="2.8"
      y="2.8"
      width="8.4"
      height="8.4"
      strokeWidth="0.9"
      fill="none"
      stroke="url(#calIconLG_b)"
      rx="2.2"
    />
    <use href="#calIconDay" x="4.44" y="5.95" />
    <use href="#calIconDay" x="6.44" y="5.95" />
    <use href="#calIconDay" x="8.44" y="5.95" />
    <use href="#calIconDay" x="4.44" y="7.95" />
    <use href="#calIconDay" x="6.44" y="7.95" />
    <use href="#calIconDay" x="8.44" y="7.95" />
  </SVG>
);

export const DownloadIcon = ({ width = 24, height = 24, ...props }: SVGProps) => (
  <SVG {...{ ...props, width, height }} viewBox="0 0 24 24">
    <path
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M20 17v1c0 1.24-1.22 2.25-2.72 2.25H6.72c-1.5 0-2.72-1-2.72-2.25v-1M12 5v10M16 11l-4 4l-4-4"
    />
  </SVG>
);
